import { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, Legend,
  ReferenceLine,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts'
import { usePortfolio } from '@/hooks/usePortfolio'
import { useAgents, DEFAULT_PROFILE } from '@/hooks/useAgents'
import { useAccounts } from '@/hooks/useAccounts'
import PageHeader from '@/components/ui/PageHeader'
import {
  getPortfolioAllocation,
  getPortfolioConcentration,
  getPortfolioCorrelation,
  getPortfolioPerformance,
  getPortfolioRiskDecomposition,
  getPortfolioDividendSchedule,
  getPortfolioBenchmark,
  getPortfolioFactorExposure,
  getTaxWrapperSummary,
  getTaxCgtEstimate,
  getTaxAllowances,
  getFeeSummary,
  getFeeTerAnalysis,
  getAllowanceStatus,
  getCgtPlanner,
  downloadCsv,
  getDiscretionaryCurrent,
  getDiscretionarySuggestions,
  postWhatIf,
  postPriceShock,
  getStrategyRecommendations,
  getPortfolioHealth,
  getModelPortfolios,
  postTargetAllocation,
  getPortfolioCompareModels,
  getPortfolioWhatWould,
  getHouseholdSummary,
} from '@/lib/api'
import { RefreshCw, X, TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp, Plus, Minus, Target, Sliders, Pencil, Check, Trash2, Building2, Landmark, Coins, Download } from 'lucide-react'
import DataSection from '@/components/ui/DataSection'
import type {
  Account,
  Holding,
  PortfolioHolding,
  AllocationBreakdown,
  ConcentrationAnalysis,
  CorrelationMatrix,
  PerformanceData,
  RiskDecomposition,
  DividendSchedule,
  BenchmarkComparison,
  FactorExposureResult,
  TaxWrapperSummary,
  CgtEstimate,
  TaxAllowances,
  FeeSummary,
  FeeTerAnalysis,
  AllowanceStatus,
  CgtPlanner,
  DiscretionaryCurrentResponse,
  DiscretionarySuggestionsResponse,
  WhatIfResponse,
  StrategyRecommendation,
  StrategyRecommendationsResponse,
  PortfolioHealthResponse,
  ModelPortfolio,
  TargetAllocationResponse,
  RebalanceTaxSummary,
  FeasibilitySummary,
  TradeVerdict,
  ModelComparisonResponse,
  WhatWouldResponse,
  HouseholdConcentrationWarning,
  HouseholdTopHolding,
  HouseholdSummary,
} from '@/lib/types'
import { labelAssetType, labelBondSector } from '@/lib/assetLabels'
import { clsx } from 'clsx'
import TickerLink from '@/components/ui/TickerLink'
import SmartAdvisorPanel from '@/components/ui/SmartAdvisorPanel'
import TableSearch from '@/components/ui/TableSearch'

const COLORS = ['#3b82f6','#f59e0b','#8b5cf6','#10b981','#06b6d4','#f43f5e','#e879f9','#84cc16','#ec4899','#14b8a6']

// Null-safe — API fields can always be null.
function fmt(n: number | null | undefined) {
  return n == null ? '—' : `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}
function pct(n: number | null | undefined) {
  return n == null ? '—' : `${n.toFixed(1)}%`
}

const TOOLTIP_STYLE = { background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--text-primary))', borderRadius: 8 }

// ─────────────────────────────────────────────────────────────────────────────
// Account management constants
// ─────────────────────────────────────────────────────────────────────────────

const MGMT_PROVIDERS = [
  { value: 'aj_bell',             label: 'AJ Bell' },
  { value: 'hargreaves_lansdown', label: 'HL' },
  { value: 'ig_index',            label: 'IG' },
  { value: 'coinbase',            label: 'Coinbase' },
  { value: 'robinhood',           label: 'Robinhood' },
  { value: 'chase',               label: 'Chase' },
  { value: 'manual',              label: 'Manual' },
]

const MGMT_ACCOUNT_TYPES = ['SIPP', 'ISA', 'GIA', 'Trading', 'Crypto', 'Bank', 'Savings']

const MGMT_OWNERS = ['Simon', 'Unman', 'Joint']

const MGMT_ASSET_TYPES = [
  { value: 'equity',       label: 'Equity' },
  { value: 'etf',          label: 'ETF' },
  { value: 'fund',         label: 'Fund' },
  { value: 'bond',         label: 'Bond' },
  { value: 'cash',         label: 'Cash' },
  { value: 'crypto',       label: 'Crypto' },
  { value: 'real_estate',  label: 'Real Estate' },
  { value: 'liability',    label: 'Liability' },
]

const PROVIDER_ICON_MAP: Record<string, typeof Building2> = {
  aj_bell: Landmark,
  hargreaves_lansdown: Landmark,
  ig_index: TrendingUp,
  robinhood: TrendingUp,
  coinbase: Coins,
  chase: Building2,
  manual: Coins,
}

function fmtPrice(n: number | null | undefined) {
  if (n == null) return '-'
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Account Form Modal (add + edit)
// ─────────────────────────────────────────────────────────────────────────────

interface AccountFormModalProps {
  initial?: Account
  onClose: () => void
}

function AccountFormModal({ initial, onClose }: AccountFormModalProps) {
  const { createAccount, updateAccount } = useAccounts()
  const isEdit = !!initial
  const [name, setName] = useState(initial?.name ?? '')
  const [provider, setProvider] = useState(initial?.provider ?? 'manual')
  const [accountType, setAccountType] = useState(initial?.account_type ?? 'GIA')
  const [owner, setOwner] = useState(initial?.owner ?? 'Unman')
  const [currency, setCurrency] = useState(initial?.currency ?? 'GBP')
  const [discretionary, setDiscretionary] = useState(initial?.discretionary ?? false)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    if (isEdit) {
      await updateAccount(initial!.id, { name: name.trim(), provider, account_type: accountType, owner, currency, discretionary } as any)
    } else {
      await createAccount({ name: name.trim(), provider, account_type: accountType, owner, currency, discretionary })
    }
    setSaving(false)
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit account dialog' : 'Add account dialog'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <form
        onSubmit={handleSubmit}
        className="card w-full max-w-sm sm:max-w-md p-6 animate-fade-in space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-text-primary text-lg">{isEdit ? 'Edit Account' : 'Add Account'}</h2>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="block text-xs text-text-muted mb-1" htmlFor="af-name">Account Name</label>
          <input
            id="af-name"
            className="input w-full"
            placeholder="e.g. AJ Bell ISA"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="af-provider">Provider</label>
            <select id="af-provider" className="input w-full" value={provider} onChange={e => setProvider(e.target.value)}>
              {MGMT_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="af-type">Account Type</label>
            <select id="af-type" className="input w-full" value={accountType} onChange={e => setAccountType(e.target.value)}>
              {MGMT_ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="af-owner">Owner</label>
            <select id="af-owner" className="input w-full" value={owner} onChange={e => setOwner(e.target.value)}>
              {MGMT_OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="af-currency">Currency</label>
            <select id="af-currency" className="input w-full" value={currency} onChange={e => setCurrency(e.target.value)}>
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={discretionary}
            onChange={e => setDiscretionary(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm text-text-secondary">Discretionary (you can trade this account)</span>
        </label>

        <button type="submit" disabled={saving || !name.trim()} className="btn-primary w-full gap-2">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Account')}
        </button>
      </form>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Holding Form Modal (add only — inline edit for updates)
// ─────────────────────────────────────────────────────────────────────────────

interface HoldingFormModalProps {
  account: Account
  onClose: () => void
}

function HoldingFormModal({ account, onClose }: HoldingFormModalProps) {
  const { addHolding } = useAccounts()
  const [name, setName] = useState('')
  const [symbol, setSymbol] = useState('')
  const [quantity, setQuantity] = useState('')
  const [avgCost, setAvgCost] = useState('')
  const [currentPrice, setCurrentPrice] = useState('')
  const [currency, setCurrency] = useState(account.currency)
  const [assetType, setAssetType] = useState('')
  const [saving, setSaving] = useState(false)

  const computedValue = () => {
    const qty = parseFloat(quantity) || 0
    const price = parseFloat(currentPrice) || parseFloat(avgCost) || 0
    return qty * price
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !quantity) return
    setSaving(true)
    await addHolding(account.id, {
      name: name.trim(),
      symbol: symbol.trim() || undefined,
      quantity: parseFloat(quantity),
      avg_cost: avgCost ? parseFloat(avgCost) : undefined,
      current_price: currentPrice ? parseFloat(currentPrice) : undefined,
      value: computedValue(),
      currency,
      asset_type: assetType || undefined,
    })
    setSaving(false)
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add holding dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <form
        onSubmit={handleSubmit}
        className="card w-full max-w-lg p-6 animate-fade-in space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-text-primary text-lg">Add Holding</h2>
            <p className="text-xs text-text-muted">{account.name}</p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-name">Name *</label>
            <input
              id="hf-name"
              className="input w-full"
              placeholder="e.g. Apple Inc"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-symbol">Symbol</label>
            <input
              id="hf-symbol"
              className="input w-full font-mono"
              placeholder="e.g. AAPL"
              value={symbol}
              onChange={e => setSymbol(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-asset-type">Asset Type</label>
            <select id="hf-asset-type" className="input w-full" value={assetType} onChange={e => setAssetType(e.target.value)}>
              <option value="">— select —</option>
              {MGMT_ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-qty">Quantity *</label>
            <input
              id="hf-qty"
              type="number"
              step="any"
              className="input w-full font-mono"
              placeholder="0"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-avg-cost">Avg Cost</label>
            <input
              id="hf-avg-cost"
              type="number"
              step="any"
              className="input w-full font-mono"
              placeholder="0.00"
              value={avgCost}
              onChange={e => setAvgCost(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-price">Current Price</label>
            <input
              id="hf-price"
              type="number"
              step="any"
              className="input w-full font-mono"
              placeholder="0.00"
              value={currentPrice}
              onChange={e => setCurrentPrice(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Est. Value</label>
            <div className="input w-full font-mono text-text-secondary bg-surface-hover/50 pointer-events-none">
              {computedValue() > 0 ? fmtPrice(computedValue()) : '—'}
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="hf-currency">Currency</label>
            <select id="hf-currency" className="input w-full" value={currency} onChange={e => setCurrency(e.target.value)}>
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        <button type="submit" disabled={saving || !name.trim() || !quantity} className="btn-primary w-full gap-2">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? 'Adding...' : 'Add Holding'}
        </button>
      </form>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Holding management row (inline edit on click)
// ─────────────────────────────────────────────────────────────────────────────

interface HoldingMgmtRowProps {
  holding: Holding
  accountId: number
}

function HoldingMgmtRow({ holding: h, accountId }: HoldingMgmtRowProps) {
  const { updateHolding, deleteHolding } = useAccounts()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [qty, setQty] = useState(String(h.quantity))
  const [avgCost, setAvgCost] = useState(h.avg_cost != null ? String(h.avg_cost) : '')
  const [currentPrice, setCurrentPrice] = useState(h.current_price != null ? String(h.current_price) : '')
  const [valueStr, setValueStr] = useState(String(h.value))

  const isCashLike = h.asset_type === 'cash' || h.asset_type === 'real_estate' || h.asset_type === 'liability'
  const cost = h.avg_cost != null ? h.avg_cost * h.quantity : null
  const pnl = cost != null ? h.value - cost : null

  const handleSave = async () => {
    setSaving(true)
    const patch: Record<string, unknown> = {}
    if (isCashLike) {
      const newValue = parseFloat(valueStr)
      if (!Number.isNaN(newValue)) { patch.value = newValue; patch.quantity = newValue }
    } else {
      patch.quantity = parseFloat(qty) || h.quantity
      patch.avg_cost = avgCost ? parseFloat(avgCost) : null
      if (currentPrice) patch.current_price = parseFloat(currentPrice)
    }
    await updateHolding(accountId, h.id, patch)
    setSaving(false)
    setEditing(false)
  }

  const handleCancel = () => {
    setQty(String(h.quantity))
    setAvgCost(h.avg_cost != null ? String(h.avg_cost) : '')
    setCurrentPrice(h.current_price != null ? String(h.current_price) : '')
    setValueStr(String(h.value))
    setEditing(false)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!window.confirm(`Delete "${h.name}"? This cannot be undone.`)) return
    await deleteHolding(accountId, h.id)
  }

  return (
    <tr
      className={clsx(
        'border-b border-border last:border-0 transition-colors group',
        editing ? 'bg-blue/5' : 'hover:bg-surface-hover cursor-pointer',
      )}
      onClick={() => { if (!editing) setEditing(true) }}
      title={editing ? undefined : 'Click to edit'}
    >
      {/* Name */}
      <td className="py-2.5 px-4">
        <p className="text-sm text-text-primary">{h.name}</p>
        {h.symbol && <p className="text-xs text-text-muted font-mono">{h.symbol}</p>}
      </td>

      {/* Type */}
      <td className="py-2.5 px-4 hidden md:table-cell">
        {h.asset_type && (
          <span className="badge badge-blue text-[10px]">
            {h.asset_type === 'bond' ? labelBondSector(h.sector) : labelAssetType(h.asset_type)}
          </span>
        )}
      </td>

      {/* Quantity */}
      <td className="py-2.5 px-4 text-sm text-right font-mono" onClick={e => editing && e.stopPropagation()}>
        {editing && !isCashLike ? (
          <input
            type="number" step="any"
            className="input w-24 text-right font-mono text-sm ml-auto"
            value={qty} onChange={e => setQty(e.target.value)}
          />
        ) : (
          <span className="text-text-primary">{(h.quantity ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 4 })}</span>
        )}
      </td>

      {/* Avg Cost */}
      <td className="py-2.5 px-4 text-sm text-right font-mono hidden sm:table-cell" onClick={e => editing && e.stopPropagation()}>
        {editing && !isCashLike ? (
          <input
            type="number" step="any"
            className="input w-24 text-right font-mono text-sm ml-auto"
            placeholder="0.00" value={avgCost} onChange={e => setAvgCost(e.target.value)}
          />
        ) : (
          <span className="text-text-secondary">{fmtPrice(h.avg_cost)}</span>
        )}
      </td>

      {/* Current Price */}
      <td className="py-2.5 px-4 text-sm text-right font-mono hidden sm:table-cell" onClick={e => editing && e.stopPropagation()}>
        {editing && !isCashLike ? (
          <input
            type="number" step="any"
            className="input w-24 text-right font-mono text-sm ml-auto"
            placeholder="0.00" value={currentPrice} onChange={e => setCurrentPrice(e.target.value)}
          />
        ) : (
          <span className="text-text-secondary">{fmtPrice(h.current_price)}</span>
        )}
      </td>

      {/* Value */}
      <td className="py-2.5 px-4 text-sm text-right font-bold" onClick={e => editing && e.stopPropagation()}>
        {editing && isCashLike ? (
          <input
            type="number"
            className="input w-28 text-right font-mono text-sm py-0.5"
            value={valueStr} onChange={e => setValueStr(e.target.value)}
            aria-label="Value"
          />
        ) : (
          <span className="text-text-primary">{fmt(h.value)}</span>
        )}
      </td>

      {/* P&L */}
      <td className="py-2.5 px-4 text-right hidden md:table-cell">
        {pnl != null ? (
          <span className={clsx('text-sm font-mono', pnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
            {pnl >= 0 ? '+' : ''}{fmt(pnl)}
          </span>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        )}
      </td>

      {/* Actions */}
      <td className="py-2.5 px-3 text-right" onClick={e => e.stopPropagation()}>
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={handleSave} disabled={saving}
              className="btn-ghost p-1.5 text-emerald hover:bg-emerald/10"
              aria-label="Save changes"
            >
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
            </button>
            <button onClick={handleCancel} className="btn-ghost p-1.5 text-text-muted" aria-label="Cancel">
              <X size={13} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); setEditing(true) }}
              className="btn-ghost p-1.5 text-text-muted hover:text-blue"
              aria-label={`Edit ${h.name}`}
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={handleDelete}
              className="btn-ghost p-1.5 text-text-muted hover:text-rose-light hover:bg-rose/10"
              aria-label={`Delete ${h.name}`}
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Account Card (expandable, with edit + add-holding)
// ─────────────────────────────────────────────────────────────────────────────

function AccountCard({ account }: { account: Account }) {
  const { holdings, fetchHoldings } = useAccounts()
  const [expanded, setExpanded] = useState(false)
  const [showEditAccount, setShowEditAccount] = useState(false)
  const [showAddHolding, setShowAddHolding] = useState(false)

  const ProviderIcon = PROVIDER_ICON_MAP[account.provider] ?? Building2
  const accountHoldings = holdings[account.id] ?? []

  const handleExpand = () => {
    if (!expanded) fetchHoldings(account.id)
    setExpanded(v => !v)
  }

  return (
    <>
      {showEditAccount && (
        <AccountFormModal
          initial={account}
          onClose={() => setShowEditAccount(false)}
        />
      )}
      {showAddHolding && (
        <HoldingFormModal
          account={account}
          onClose={() => { setShowAddHolding(false); fetchHoldings(account.id) }}
        />
      )}

      <div className="card overflow-hidden">
        {/* Header row */}
        <div
          className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-surface-hover transition-colors"
          onClick={handleExpand}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={`${account.name} – ${fmt(account.total_value)}`}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpand() } }}
        >
          <ProviderIcon size={18} className="text-text-muted shrink-0" aria-hidden />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-text-primary">{account.name}</span>
              <span className="badge badge-blue text-[10px]">{account.account_type}</span>
              {account.discretionary && (
                <span className="badge badge-green text-[10px]">Discretionary</span>
              )}
              <span className="text-xs text-text-muted">{account.owner}</span>
            </div>
            <p className="text-xs text-text-muted mt-0.5">{account.holdings_count} holding{account.holdings_count !== 1 ? 's' : ''}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm font-bold text-text-primary font-mono">{fmt(account.total_value)}</p>
          </div>
          {/* Edit icon */}
          <button
            onClick={e => { e.stopPropagation(); setShowEditAccount(true) }}
            className="btn-ghost p-1.5 text-text-muted hover:text-blue opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            aria-label={`Edit ${account.name}`}
            title="Edit account"
          >
            <Pencil size={14} />
          </button>
          {expanded ? <ChevronUp size={16} className="text-text-muted shrink-0" /> : <ChevronDown size={16} className="text-text-muted shrink-0" />}
        </div>

        {/* Holdings table */}
        {expanded && (
          <div className="border-t border-border">
            <div className="overflow-x-auto">
              <table className="w-full" aria-label={`Holdings for ${account.name}`}>
                <thead>
                  <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
                    <th className="py-2 px-4 text-left font-medium">Asset</th>
                    <th className="py-2 px-4 text-left font-medium hidden md:table-cell">Type</th>
                    <th className="py-2 px-4 text-right font-medium">Qty</th>
                    <th className="py-2 px-4 text-right font-medium hidden sm:table-cell">Avg Cost</th>
                    <th className="py-2 px-4 text-right font-medium hidden sm:table-cell">Price</th>
                    <th className="py-2 px-4 text-right font-medium">Value</th>
                    <th className="py-2 px-4 text-right font-medium hidden md:table-cell">P&L</th>
                    <th className="py-2 px-3 text-right font-medium w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {accountHoldings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-sm text-text-muted">
                        No holdings yet. Add one below.
                      </td>
                    </tr>
                  ) : (
                    accountHoldings.map(h => (
                      <HoldingMgmtRow key={h.id} holding={h} accountId={account.id} />
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Add holding button */}
            <div className="px-5 py-3 border-t border-border">
              <button
                onClick={() => setShowAddHolding(true)}
                className="btn-ghost text-xs gap-1.5 text-blue"
              >
                <Plus size={13} aria-hidden />
                Add Holding
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Accounts Tab
// ─────────────────────────────────────────────────────────────────────────────

function AccountsTab() {
  const { accounts, fetchAccounts } = useAccounts()
  const [showAddAccount, setShowAddAccount] = useState(false)

  useEffect(() => { fetchAccounts() }, []) // eslint-disable-line

  const totalValue = accounts.reduce((s, a) => s + a.total_value, 0)

  return (
    <div className="space-y-4">
      {showAddAccount && (
        <AccountFormModal onClose={() => { setShowAddAccount(false); fetchAccounts() }} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-text-secondary">
            {accounts.length} account{accounts.length !== 1 ? 's' : ''}
            <span className="ml-2 font-semibold text-text-primary">{fmt(totalValue)}</span>
          </p>
        </div>
        <button onClick={() => setShowAddAccount(true)} className="btn-primary gap-2 text-sm">
          <Plus size={14} aria-hidden />
          Add Account
        </button>
      </div>

      {/* Account cards */}
      {accounts.length === 0 ? (
        <div className="card p-10 text-center text-sm text-text-muted">
          No accounts yet. Add your first account to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map(a => (
            <div key={a.id} className="group">
              <AccountCard account={a} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function RiskBadge({ level }: { level: string }) {
  const cls = level === 'High' ? 'badge-red' : level === 'Medium' ? 'badge-amber' : 'badge-green'
  return <span className={cls}>{level}</span>
}

function RebalanceModal({ onClose }: { onClose: () => void }) {
  const { optimize, optimizing, optimization } = usePortfolio()

  useEffect(() => { optimize(DEFAULT_PROFILE) }, []) // eslint-disable-line

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Portfolio rebalance dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="card w-full max-w-lg p-6 animate-fade-in">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-text-primary text-lg">Portfolio Rebalance</h2>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>

        {optimizing ? (
          <div className="py-8 text-center text-text-secondary text-sm">
            <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-blue" />
            Running optimisation...
          </div>
        ) : optimization ? (
          <div className="space-y-4">
            <div className={clsx('px-3 py-2.5 rounded-lg text-sm font-medium',
              optimization.action === 'no_action' ? 'bg-emerald/10 text-emerald-light' : 'bg-amber/10 text-amber-light'
            )}>
              {optimization.action === 'no_action' ? 'No rebalancing required' : 'Rebalancing recommended'}
            </div>
            <p className="text-sm text-text-secondary leading-relaxed">{optimization.recommendation}</p>
            {optimization.trades.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-text-muted uppercase tracking-wider">Suggested trades</p>
                {optimization.trades.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 bg-surface-DEFAULT rounded-lg">
                    <span className={clsx('badge text-xs font-mono', t.action === 'SELL' ? 'badge-red' : 'badge-green')}>
                      {t.action}
                    </span>
                    <span className="text-sm text-text-primary font-medium">{t.asset}</span>
                    <span className="ml-auto text-xs text-text-secondary">{(t.pct ?? 0).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-text-muted border-t border-border pt-3">{optimization.rationale}</p>
          </div>
        ) : null}

        <button onClick={onClose} className="btn-secondary w-full mt-6">Close</button>
      </div>
    </div>
  )
}

type SortKey = 'value' | 'weight' | 'change_pct' | 'name' | 'sector'

function HoldingsTable({
  holdings,
  limit,
}: {
  holdings: PortfolioHolding[]
  limit: number
}) {
  const [sortKey, setSortKey] = useState<SortKey>('value')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [searchQuery, setSearchQuery] = useState('')
  const totalValue = holdings.reduce((s, h) => s + h.value, 0) || 1

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const filtered = searchQuery
    ? holdings.filter(h =>
        h.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        h.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : holdings

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0
    if (sortKey === 'name') cmp = a.name.localeCompare(b.name)
    else if (sortKey === 'sector') cmp = a.sector.localeCompare(b.sector)
    else cmp = (a[sortKey] as number) - (b[sortKey] as number)
    return sortDir === 'desc' ? -cmp : cmp
  })

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null
    return sortDir === 'desc' ? <ChevronDown size={12} className="inline ml-0.5" /> : <ChevronUp size={12} className="inline ml-0.5" />
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <h2 className="panel-title">Holdings</h2>
        <TableSearch placeholder="Search ticker or name..." onSearch={setSearchQuery} />
      </div>
      <div className="overflow-x-auto table-responsive table-sticky-first">
        <table className="table-pro w-full" aria-label="Portfolio holdings">
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => toggleSort('name')}>
                Asset <SortIcon col="name" />
              </th>
              <th className="hidden md:table-cell cursor-pointer" onClick={() => toggleSort('sector')}>
                Sector <SortIcon col="sector" />
              </th>
              <th className="hidden lg:table-cell">Region</th>
              <th className="!text-right cursor-pointer" onClick={() => toggleSort('value')}>
                Value <SortIcon col="value" />
              </th>
              <th className="!text-right hidden sm:table-cell">Risk</th>
              <th className="!text-right hidden sm:table-cell">P&L</th>
              <th className="!text-right cursor-pointer" onClick={() => toggleSort('change_pct')}>
                Change <SortIcon col="change_pct" />
              </th>
              <th className="!text-right cursor-pointer" onClick={() => toggleSort('weight')}>
                Weight <SortIcon col="weight" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(h => {
              const pnl = h.value - (h.shares * h.avg_cost)
              const overLimit = h.value > limit
              return (
                <tr key={h.id}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {overLimit && (
                        <AlertTriangle size={13} className="text-amber shrink-0" aria-label="Exceeds position limit" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-text-primary">{h.name}</p>
                        <p className="text-xs"><TickerLink ticker={h.symbol} /></p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs text-text-secondary hidden md:table-cell">{h.sector}</td>
                  <td className="py-3 px-4 text-xs text-text-secondary hidden lg:table-cell">{h.region}</td>
                  <td className="py-3 px-4 text-sm text-text-primary font-mono text-right">{fmt(h.value)}</td>
                  <td className="py-3 px-4 text-right hidden sm:table-cell">
                    <RiskBadge level={h.risk_level} />
                  </td>
                  <td className="py-3 px-4 text-sm text-right hidden sm:table-cell">
                    <span className={pnl >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                      {pnl >= 0 ? '+' : ''}{fmt(pnl)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right">
                    <span className={h.change_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                      {h.change_pct >= 0 ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}
                      {pct(h.change_pct)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-text-secondary text-right">{pct(h.weight)}</td>
                </tr>
              )
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-sm text-text-muted">
                  No holdings match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Allocation Donuts
// ─────────────────────────────────────────────────────────────────────────────

function DonutChart({
  title,
  data,
}: {
  title: string
  data: { label: string; value: number; pct: number }[]
}) {
  const pieData = data.map((d, i) => ({ ...d, color: COLORS[i % COLORS.length] }))
  return (
    <div className="card p-5">
      <h3 className="panel-title mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={78}
            dataKey="value"
            nameKey="label"
            paddingAngle={2}
          >
            {pieData.map((entry, i) => (
              <Cell key={i} fill={entry.color} stroke="transparent" />
            ))}
          </Pie>
          <Tooltip
            formatter={(v: number, _: string, props: any) => [
              `${fmt(v)} (${props.payload.pct}%)`,
              props.payload.label,
            ]}
            contentStyle={TOOLTIP_STYLE}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
        {pieData.map((d, i) => (
          <span key={i} className="flex items-center gap-1.5 text-xs text-text-secondary">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
            {d.label} ({d.pct}%)
          </span>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Concentration
// ─────────────────────────────────────────────────────────────────────────────

function ConcentrationSection({ data }: { data: ConcentrationAnalysis }) {
  const hhiColor = data.hhi_label === 'High' ? 'text-rose-light' : data.hhi_label === 'Moderate' ? 'text-amber' : 'text-emerald'
  const barData = data.top_holdings.map((h, i) => ({
    symbol: h.symbol,
    weight: h.weight_pct,
    fill: COLORS[i % COLORS.length],
  }))

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">Concentration</h3>
        <div className="text-right">
          <span className="text-xs text-text-muted">HHI: </span>
          <span className={clsx('text-sm font-bold', hhiColor)}>{data.hhi}</span>
          <span className={clsx('text-xs ml-1', hhiColor)}>({data.hhi_label})</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 10, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
          <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="symbol" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={50} />
          <Tooltip
            formatter={(v: number) => [`${v.toFixed(1)}%`, 'Weight']}
            contentStyle={TOOLTIP_STYLE}
          />
          <Bar dataKey="weight" radius={[0, 4, 4, 0]}>
            {barData.map((entry, i) => (
              <Cell key={i} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Household Concentration Risk (from /api/accounts/summary)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a percentage (0–100) to the nearest safe Tailwind width class.
 * Explicit string literals are required so Tailwind's purge pass keeps them.
 */
function pctToWidthClass(p: number): string {
  if (p >= 95) return 'w-full'
  if (p >= 87) return 'w-11/12'
  if (p >= 81) return 'w-5/6'
  if (p >= 75) return 'w-3/4'
  if (p >= 68) return 'w-2/3'
  if (p >= 56) return 'w-3/5'
  if (p >= 44) return 'w-1/2'
  if (p >= 37) return 'w-2/5'
  if (p >= 31) return 'w-1/3'
  if (p >= 25) return 'w-1/4'
  if (p >= 19) return 'w-1/5'
  if (p >= 13) return 'w-1/6'
  return 'w-1/12'
}

function ConcentrationRiskSection({
  warnings,
}: {
  warnings: HouseholdConcentrationWarning[]
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle size={15} className="text-amber shrink-0" aria-hidden />
        <h3 className="panel-title">Concentration Risk</h3>
        <span className="text-xs text-text-muted ml-auto">&gt;10% of tracked investments</span>
      </div>
      <div className="space-y-3">
        {warnings.map((w, i) => {
          const isHigh = w.severity === 'high'
          return (
            <div key={i} className={clsx('rounded-lg p-3 border', isHigh ? 'bg-rose-900/15 border-rose-800/40' : 'bg-amber/10 border-amber/30')}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle
                    size={12}
                    className={clsx('shrink-0', isHigh ? 'text-rose-light' : 'text-amber')}
                    aria-label={isHigh ? 'High concentration' : 'Moderate concentration'}
                  />
                  <span className={clsx('text-sm font-semibold', isHigh ? 'text-rose-light' : 'text-amber')}>
                    {w.ticker ?? w.name}
                  </span>
                  {w.ticker && w.ticker !== w.name && (
                    <span className="text-xs text-text-muted truncate">{w.name}</span>
                  )}
                </div>
                <div className="text-right shrink-0 ml-3">
                  <span className={clsx('text-sm font-bold font-mono', isHigh ? 'text-rose-light' : 'text-amber')}>
                    {(w.pct_of_total ?? 0).toFixed(1)}%
                  </span>
                  <span className="text-xs text-text-muted ml-1.5">{fmt(w.value)}</span>
                </div>
              </div>
              {/* Weight bar — width via Tailwind fraction class, no inline style needed */}
              <div className="h-1.5 bg-surface-hover rounded-full overflow-hidden mb-1.5" role="presentation">
                <div className={clsx('h-full rounded-full transition-all', pctToWidthClass(w.pct_of_total), isHigh ? 'bg-rose-light' : 'bg-amber')} />
              </div>
              <p className="text-xs text-text-muted">{w.account}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TopHoldingsTable({ holdings }: { holdings: HouseholdTopHolding[] }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3 border-b border-border">
        <h3 className="panel-title">Top 10 Holdings</h3>
        <p className="text-xs text-text-muted mt-0.5">Largest positions across all accounts, by value</p>
      </div>
      <div className="overflow-x-auto">
        <table className="table-pro w-full" aria-label="Top 10 holdings by value">
          <thead>
            <tr>
              <th>#</th>
              <th>Asset</th>
              <th className="hidden md:table-cell">Account</th>
              <th className="!text-right">Value</th>
              <th className="!text-right">% of Tracked</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h, i) => {
              const isHigh = h.pct_of_total > 20
              const isMed = !isHigh && h.pct_of_total > 10
              return (
                <tr key={i}>
                  <td className="py-2 px-4 text-xs text-text-muted font-mono">{i + 1}</td>
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-1.5">
                      {(isHigh || isMed) && (
                        <AlertTriangle
                          size={11}
                          className={isHigh ? 'text-rose-light shrink-0' : 'text-amber shrink-0'}
                          aria-label={isHigh ? 'High concentration' : 'Moderate concentration'}
                        />
                      )}
                      <div>
                        <span className="text-sm font-medium text-text-primary">{h.name}</span>
                        {h.ticker && (
                          <span className="text-xs text-text-muted font-mono ml-1.5">{h.ticker}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 text-xs text-text-muted hidden md:table-cell">{h.account}</td>
                  <td className="py-2 px-4 text-right text-sm font-mono text-text-primary">{fmt(h.value)}</td>
                  <td className="py-2 px-4 text-right">
                    <span className={clsx(
                      'text-sm font-mono font-semibold',
                      isHigh ? 'text-rose-light' : isMed ? 'text-amber' : 'text-text-secondary',
                    )}>
                      {(h.pct_of_total ?? 0).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Performance Chart
// ─────────────────────────────────────────────────────────────────────────────

function PerformanceSection({ data }: { data: PerformanceData }) {
  const rs = data.return_stats
  return (
    <div className="card p-5">
      <h3 className="panel-title mb-4">Performance vs S&P 500</h3>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data.cumulative} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={d => d.slice(5)}
            interval={Math.floor(data.cumulative.length / 6)}
          />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={42} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="portfolio" stroke="#3b82f6" strokeWidth={2} dot={false} name="Portfolio" />
          <Line type="monotone" dataKey="benchmark" stroke="#94a3b8" strokeWidth={1.5} dot={false} name="S&P 500" strokeDasharray="4 2" />
        </LineChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div className="text-center">
          <p className="text-xs text-text-muted">Daily avg</p>
          <p className={clsx('text-sm font-bold', rs.daily_avg >= 0 ? 'text-emerald' : 'text-rose-light')}>
            {(rs.daily_avg ?? 0) >= 0 ? '+' : ''}{(rs.daily_avg ?? 0).toFixed(3)}%
          </p>
          <p className="text-xs text-text-muted">std: {(rs.daily_std ?? 0).toFixed(3)}%</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted">Weekly avg</p>
          <p className={clsx('text-sm font-bold', rs.weekly_avg >= 0 ? 'text-emerald' : 'text-rose-light')}>
            {(rs.weekly_avg ?? 0) >= 0 ? '+' : ''}{(rs.weekly_avg ?? 0).toFixed(2)}%
          </p>
          <p className="text-xs text-text-muted">std: {(rs.weekly_std ?? 0).toFixed(2)}%</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted">Monthly avg</p>
          <p className={clsx('text-sm font-bold', rs.monthly_avg >= 0 ? 'text-emerald' : 'text-rose-light')}>
            {(rs.monthly_avg ?? 0) >= 0 ? '+' : ''}{(rs.monthly_avg ?? 0).toFixed(2)}%
          </p>
          <p className="text-xs text-text-muted">std: {(rs.monthly_std ?? 0).toFixed(2)}%</p>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Risk Decomposition
// ─────────────────────────────────────────────────────────────────────────────

function RiskSection({ data }: { data: RiskDecomposition }) {
  const barData = data.contributions.map((c, i) => ({
    symbol: c.symbol,
    risk: c.risk_contribution_pct,
    weight: c.weight_pct,
    fill: COLORS[i % COLORS.length],
  }))

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">Risk Decomposition</h3>
        <div className="flex gap-4 text-xs text-text-muted">
          <span>Vol: <strong className="text-text-primary">{(data.portfolio_volatility ?? 0).toFixed(1)}%</strong></span>
          <span>VaR 95%: <strong className="text-text-primary">{fmt(data.portfolio_var_95)}</strong></span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={barData} margin={{ top: 0, right: 10, bottom: 0, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="symbol" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
          <Tooltip
            formatter={(v: number, name: string) => [`${v.toFixed(1)}%`, name === 'risk' ? 'Risk Contrib.' : 'Weight']}
            contentStyle={TOOLTIP_STYLE}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="risk" name="Risk Contrib." radius={[4, 4, 0, 0]}>
            {barData.map((entry, i) => (
              <Cell key={i} fill={entry.fill} />
            ))}
          </Bar>
          <Bar dataKey="weight" name="Weight" fill="#475569" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Correlation Heatmap
// ─────────────────────────────────────────────────────────────────────────────

function CorrelationHeatmap({ data }: { data: CorrelationMatrix }) {
  const corrColor = (v: number) => {
    if (v >= 0.8) return 'bg-red-500/60'
    if (v >= 0.5) return 'bg-orange-500/50'
    if (v >= 0.2) return 'bg-amber-500/30'
    if (v >= -0.2) return 'bg-text-muted/20'
    return 'bg-blue-500/40'
  }

  return (
    <div className="card p-5">
      <h3 className="panel-title mb-4">Correlation Matrix</h3>
      <div className="overflow-x-auto">
        <table className="text-xs" aria-label="Correlation matrix">
          <thead>
            <tr>
              <th className="py-1 px-2 text-text-muted font-medium text-left" />
              {data.symbols.map(s => (
                <th key={s} className="py-1 px-2 text-text-muted font-medium text-center min-w-[52px]">{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.symbols.map((rowSym, i) => (
              <tr key={rowSym}>
                <td className="py-1 px-2 text-text-muted font-medium">{rowSym}</td>
                {data.matrix[i].map((val, j) => (
                  <td key={j} className={clsx('py-1 px-2 text-center rounded-sm', corrColor(val))}>
                    <span className="text-text-primary font-mono">{val.toFixed(2)}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-3 mt-3 text-xs text-text-muted">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500/40" /> Negative</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-text-muted/20" /> Low</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/30" /> Moderate</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500/50" /> High</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/60" /> Very High</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Dividend Schedule
// ─────────────────────────────────────────────────────────────────────────────

function DividendSection({ data }: { data: DividendSchedule }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">Dividend Schedule</h3>
        <span className="text-xs text-text-muted">
          Est. annual income: <strong className="text-emerald">{fmt(data.annual_income_est)}</strong>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label="Upcoming dividends">
          <thead>
            <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
              <th className="py-2 px-3 text-left font-medium">Asset</th>
              <th className="py-2 px-3 text-right font-medium">Ex-Date</th>
              <th className="py-2 px-3 text-right font-medium">Amount</th>
              <th className="py-2 px-3 text-right font-medium">Yield</th>
            </tr>
          </thead>
          <tbody>
            {data.upcoming.map((d, i) => (
              <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                <td className="py-2 px-3">
                  <span className="text-text-primary font-medium">{d.name}</span>
                  <span className="text-text-muted text-xs ml-1.5 font-mono">{d.symbol}</span>
                </td>
                <td className="py-2 px-3 text-right text-text-secondary">{d.ex_date}</td>
                <td className="py-2 px-3 text-right text-emerald font-mono">{fmt(d.amount)}</td>
                <td className="py-2 px-3 text-right text-text-secondary">{(d.yield_pct ?? 0).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Rebalance Tab — Discretionary accounts only
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_COLORS: Record<string, string> = {
  high: 'text-rose-light',
  medium: 'text-amber',
  low: 'text-blue',
}
const SEVERITY_BG: Record<string, string> = {
  high: 'bg-rose-900/20 border-rose-800/40',
  medium: 'bg-amber/10 border-amber/30',
  low: 'bg-blue/10 border-blue/30',
}

function RebalanceTab() {
  const [current, setCurrent] = useState<DiscretionaryCurrentResponse | null>(null)
  const [suggestions, setSuggestions] = useState<DiscretionarySuggestionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // What-if state
  const [wiAction, setWiAction] = useState<'buy' | 'sell'>('sell')
  const [wiTicker, setWiTicker] = useState('')
  const [wiQty, setWiQty] = useState('')
  const [wiAmount, setWiAmount] = useState('')
  const [wiMode, setWiMode] = useState<'qty' | 'amount'>('qty')
  const [wiResult, setWiResult] = useState<WhatIfResponse | null>(null)
  const [wiLoading, setWiLoading] = useState(false)
  const [wiError, setWiError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getDiscretionaryCurrent().then(setCurrent).catch(() => {}),
      getDiscretionarySuggestions().then(setSuggestions).catch(() => {}),
    ])
      .catch(() => setError('Failed to load rebalancing data'))
      .finally(() => setLoading(false))
  }, [])

  const runWhatIf = async () => {
    if (!wiTicker.trim()) return
    setWiLoading(true)
    setWiError(null)
    setWiResult(null)
    try {
      const body: { action: string; ticker: string; quantity?: number; amount?: number } = {
        action: wiAction,
        ticker: wiTicker.trim().toUpperCase(),
      }
      if (wiMode === 'qty') {
        body.quantity = parseFloat(wiQty) || 0
      } else {
        body.amount = parseFloat(wiAmount) || 0
      }
      const result = await postWhatIf(body)
      setWiResult(result)
    } catch (e: any) {
      setWiError(e?.response?.data?.detail || 'What-if calculation failed')
    } finally {
      setWiLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-text-muted text-sm">
        <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue" />
        Loading discretionary portfolio...
      </div>
    )
  }

  if (error) {
    return <div className="card p-4 text-rose-light text-sm">{error}</div>
  }

  return (
    <div className="space-y-5">

      {/* Header: total + account breakdown */}
      {current && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="card p-4">
            <p className="text-xs text-text-muted uppercase tracking-wider">Discretionary Total</p>
            <p className="text-2xl font-bold text-text-primary mt-1">{fmt(current.total_value)}</p>
            <p className="text-xs text-text-muted mt-0.5">
              {current.holding_count} holdings across {(current.account_summary ?? []).length} account{(current.account_summary ?? []).length === 1 ? '' : 's'}
            </p>
          </div>
          {current.account_summary.map(a => (
            <div key={a.account_id} className="card p-4">
              <p className="text-xs text-text-muted truncate">{a.account_name}</p>
              <p className="text-lg font-bold text-text-primary mt-1">{fmt(a.value)}</p>
              <p className="text-xs text-text-muted mt-0.5">{pct(a.pct_of_discretionary)} of discretionary</p>
            </div>
          ))}
        </div>
      )}

      {/* Concentration warnings */}
      {current && current.concentration_warnings.length > 0 && (
        <div className="card p-4 border border-amber/30 bg-amber/5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={15} className="text-amber shrink-0" />
            <h3 className="text-sm font-semibold text-amber">Concentration Warnings</h3>
          </div>
          <div className="space-y-1.5">
            {current.concentration_warnings.map((w, i) => (
              <p key={i} className="text-xs text-text-secondary">{w.message}</p>
            ))}
          </div>
        </div>
      )}

      {/* Holdings table */}
      {current && current.holdings.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h3 className="panel-title">Discretionary Holdings</h3>
            <span className="text-xs text-text-muted">
              Accounts: {(current.account_summary ?? []).map(a => a.account_name).join(' · ') || '—'}
            </span>
          </div>
          <div className="overflow-x-auto table-responsive table-sticky-first">
            <table className="w-full" aria-label="Discretionary holdings">
              <thead>
                <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
                  <th className="py-2.5 px-4 text-left font-medium">Asset</th>
                  <th className="py-2.5 px-4 text-left font-medium hidden md:table-cell">Sector</th>
                  <th className="py-2.5 px-4 text-left font-medium hidden lg:table-cell">Account</th>
                  <th className="py-2.5 px-4 text-right font-medium">Value</th>
                  <th className="py-2.5 px-4 text-right font-medium">Weight</th>
                </tr>
              </thead>
              <tbody>
                {current.holdings.map(h => (
                  <tr key={h.holding_id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        {h.concentrated && (
                          <AlertTriangle size={12} className="text-amber shrink-0" aria-label="Concentrated position" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-text-primary">{h.name}</p>
                          {h.ticker && <p className="text-xs text-text-muted font-mono">{h.ticker}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-text-secondary hidden md:table-cell">{h.sector}</td>
                    <td className="py-2.5 px-4 text-xs text-text-muted hidden lg:table-cell truncate max-w-[160px]">{h.account_name}</td>
                    <td className="py-2.5 px-4 text-sm font-mono text-text-primary text-right">{fmt(h.value)}</td>
                    <td className="py-2.5 px-4 text-sm text-right">
                      <span className={h.concentrated ? 'text-amber font-bold' : 'text-text-secondary'}>
                        {pct(h.weight_pct)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions && suggestions.suggestions.length > 0 && (
        <div className="card p-5">
          <h3 className="panel-title mb-4">
            Rule-Based Suggestions ({suggestions.suggestions.length})
          </h3>
          <div className="space-y-3">
            {suggestions.suggestions.map((s, i) => (
              <div
                key={i}
                className={clsx('rounded-lg border p-3.5', SEVERITY_BG[s.severity] ?? 'bg-surface-DEFAULT border-border')}
              >
                <div className="flex items-start gap-2 mb-1">
                  <span className={clsx('text-xs font-bold uppercase tracking-wider shrink-0 mt-0.5', SEVERITY_COLORS[s.severity])}>
                    {s.severity}
                  </span>
                  <p className="text-sm font-semibold text-text-primary leading-snug">{s.title}</p>
                </div>
                <p className="text-xs text-text-secondary leading-relaxed pl-8">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestions && suggestions.suggestions.length === 0 && (
        <div className="card p-5 text-center">
          <p className="text-sm text-emerald font-medium">No rebalancing suggestions — discretionary portfolio looks well-balanced.</p>
        </div>
      )}

      {/* Breakdown donuts — breakdowns can be absent when there are no discretionary holdings */}
      {suggestions && suggestions.breakdowns && (
        <div className="grid md:grid-cols-2 gap-4">
          <DonutChart
            title="Sector Exposure (Discretionary)"
            data={suggestions.breakdowns.sector ?? []}
          />
          <DonutChart
            title="Geographic Exposure (Discretionary)"
            data={suggestions.breakdowns.region ?? []}
          />
          <DonutChart
            title="Asset Type (Discretionary)"
            data={suggestions.breakdowns.asset_type ?? []}
          />
          <DonutChart
            title="Currency Exposure (Discretionary)"
            data={suggestions.breakdowns.currency ?? []}
          />
        </div>
      )}

      {/* What-If Calculator */}
      <div className="card p-5">
        <h3 className="panel-title mb-4">What-If Calculator</h3>
        <p className="text-xs text-text-muted mb-4">
          Simulate a trade to see how it would change discretionary weights, sector exposure and concentration.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {/* Action */}
          <div>
            <label className="block text-xs text-text-muted mb-1.5">Action</label>
            <div className="flex rounded-lg overflow-hidden border border-border">
              {(['sell', 'buy'] as const).map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setWiAction(a)}
                  className={clsx(
                    'flex-1 py-2 text-sm font-medium transition-colors',
                    wiAction === a
                      ? a === 'sell' ? 'bg-rose-900/40 text-rose-light' : 'bg-emerald/20 text-emerald'
                      : 'bg-surface-DEFAULT text-text-muted hover:text-text-secondary',
                  )}
                >
                  {a.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Ticker */}
          <div>
            <label className="block text-xs text-text-muted mb-1.5">Ticker / Name</label>
            <input
              type="text"
              value={wiTicker}
              onChange={e => setWiTicker(e.target.value)}
              placeholder="e.g. BATS.L, SMT.L, BTC"
              className="input w-full text-sm"
            />
          </div>

          {/* Qty / Amount toggle */}
          <div>
            <label className="block text-xs text-text-muted mb-1.5">Size by</label>
            <div className="flex rounded-lg overflow-hidden border border-border">
              {(['qty', 'amount'] as const).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setWiMode(m)}
                  className={clsx(
                    'flex-1 py-2 text-sm font-medium transition-colors',
                    wiMode === m
                      ? 'bg-blue/20 text-blue'
                      : 'bg-surface-DEFAULT text-text-muted hover:text-text-secondary',
                  )}
                >
                  {m === 'qty' ? 'Shares' : 'GBP £'}
                </button>
              ))}
            </div>
          </div>

          {/* Qty or amount */}
          <div>
            <label className="block text-xs text-text-muted mb-1.5">
              {wiMode === 'qty' ? 'Quantity (shares)' : 'Amount (£)'}
            </label>
            <input
              type="number"
              min="0"
              value={wiMode === 'qty' ? wiQty : wiAmount}
              onChange={e => wiMode === 'qty' ? setWiQty(e.target.value) : setWiAmount(e.target.value)}
              placeholder={wiMode === 'qty' ? '100' : '5000'}
              className="input w-full text-sm"
            />
          </div>
        </div>

        <button
          onClick={runWhatIf}
          disabled={wiLoading || !wiTicker.trim()}
          className="btn-primary px-6 py-2 text-sm"
        >
          {wiLoading ? (
            <span className="flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Calculating...</span>
          ) : (
            'Run Scenario'
          )}
        </button>

        {wiError && (
          <p className="mt-3 text-xs text-rose-light">{wiError}</p>
        )}

        {/* What-if result */}
        {wiResult && (
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-surface-DEFAULT rounded-lg p-3">
                <p className="text-xs text-text-muted mb-1">Total Before</p>
                <p className="text-lg font-bold text-text-primary">{fmt(wiResult.before.total_value)}</p>
                <p className="text-xs text-text-muted">{wiResult.before.holding_count} holdings</p>
              </div>
              <div className="bg-surface-DEFAULT rounded-lg p-3">
                <p className="text-xs text-text-muted mb-1">Total After</p>
                <p className="text-lg font-bold text-text-primary">{fmt(wiResult.after.total_value)}</p>
                <p className="text-xs text-text-muted">{wiResult.after.holding_count} holdings</p>
              </div>
            </div>

            <p className="text-xs text-text-muted">
              {wiResult.action.toUpperCase()} {wiResult.quantity} shares of {wiResult.ticker} — est.{' '}
              <span className="font-mono">{fmt(wiResult.trade_value)}</span>
            </p>

            {/* Sector comparison */}
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">Sector Exposure</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-text-muted">
                      <th className="py-1.5 px-2 text-left font-medium">Sector</th>
                      <th className="py-1.5 px-2 text-right font-medium">Before</th>
                      <th className="py-1.5 px-2 text-right font-medium">After</th>
                      <th className="py-1.5 px-2 text-right font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wiResult.after.sector.map(s => {
                      const before = wiResult.before.sector.find(b => b.label === s.label)?.pct ?? 0
                      const chg = s.pct - before
                      return (
                        <tr key={s.label} className="border-b border-border last:border-0">
                          <td className="py-1.5 px-2 text-text-secondary">{s.label}</td>
                          <td className="py-1.5 px-2 text-right font-mono text-text-muted">{before.toFixed(1)}%</td>
                          <td className="py-1.5 px-2 text-right font-mono text-text-primary">{(s.pct ?? 0).toFixed(1)}%</td>
                          <td className={clsx('py-1.5 px-2 text-right font-mono', chg > 0 ? 'text-emerald' : chg < 0 ? 'text-rose-light' : 'text-text-muted')}>
                            {chg > 0 ? '+' : ''}{chg.toFixed(1)}%
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top holdings after */}
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">Top Holdings After Trade</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-text-muted">
                      <th className="py-1.5 px-2 text-left font-medium">Asset</th>
                      <th className="py-1.5 px-2 text-right font-medium">Value</th>
                      <th className="py-1.5 px-2 text-right font-medium">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wiResult.after.top_holdings.slice(0, 8).map(h => (
                      <tr key={h.holding_id} className="border-b border-border last:border-0">
                        <td className="py-1.5 px-2">
                          <span className="text-text-primary font-medium">{h.ticker || h.name}</span>
                          {h.concentrated && <AlertTriangle size={10} className="text-amber inline ml-1" />}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-text-secondary">{fmt(h.value)}</td>
                        <td className={clsx('py-1.5 px-2 text-right font-mono', h.concentrated ? 'text-amber font-bold' : 'text-text-secondary')}>
                          {pct(h.weight_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Concentration warnings */}
            {wiResult.concentration_warnings.length > 0 && (
              <div className="border border-amber/30 bg-amber/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={13} className="text-amber" />
                  <span className="text-xs font-semibold text-amber">Concentration after trade</span>
                </div>
                {wiResult.concentration_warnings.map((w, i) => (
                  <p key={i} className="text-xs text-text-secondary">{w.message}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scenario Planner */}
      <ScenarioPlannerSection />

      {/* Target Allocation */}
      <TargetAllocationSection />

    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Scenario Planner Section — "What if MSFT drops 20%?"
// ─────────────────────────────────────────────────────────────────────────────

function ScenarioPlannerSection() {
  // Each row: ticker + change %
  const [rows, setRows] = useState([{ ticker: '', change_pct: '' }])
  const [result, setResult] = useState<import('@/lib/types').PriceShockResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const addRow = () => setRows(r => [...r, { ticker: '', change_pct: '' }])
  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i))
  const updateRow = (i: number, field: 'ticker' | 'change_pct', val: string) => {
    setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row))
    setResult(null)
    setError(null)
  }

  const valid = rows.some(r => r.ticker.trim() && r.change_pct.trim() !== '')

  const runScenario = async () => {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const scenarios = rows
        .filter(r => r.ticker.trim() && r.change_pct.trim() !== '')
        .map(r => ({
          ticker: r.ticker.trim().toUpperCase(),
          change_pct: parseFloat(r.change_pct),
        }))
      const res = await postPriceShock(scenarios)
      setResult(res)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Scenario calculation failed')
    } finally {
      setLoading(false)
    }
  }

  const netPos = result ? result.net_gain_loss >= 0 : false

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <TrendingDown size={16} className="text-rose-light shrink-0" />
        <h3 className="panel-title">Scenario Planner</h3>
        <span className="text-xs text-text-muted ml-2">
          Model any custom price shock — e.g. MSFT drops 20%, BTC drops 30%
        </span>
      </div>

      <p className="text-xs text-text-muted">
        Enter tickers and percentage changes (negative = drop, positive = rise).
        Terminal: <span className="font-mono text-blue-light">/scenario MSFT:-20 BTC:-30</span>
      </p>

      {/* Input rows */}
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={row.ticker}
              onChange={e => updateRow(i, 'ticker', e.target.value)}
              placeholder="Ticker, e.g. MSFT"
              className="input text-sm w-36"
              aria-label={`Scenario row ${i + 1} ticker`}
            />
            <div className="flex items-center gap-1 flex-1">
              <input
                type="number"
                value={row.change_pct}
                onChange={e => updateRow(i, 'change_pct', e.target.value)}
                placeholder="% change, e.g. -20"
                className="input text-sm flex-1"
                aria-label={`Scenario row ${i + 1} change percent`}
              />
              <span className="text-xs text-text-muted shrink-0">%</span>
            </div>
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="btn-ghost p-1.5 shrink-0 text-text-muted hover:text-rose-light"
                aria-label="Remove row"
              >
                <Minus size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={addRow}
          className="btn-secondary text-xs gap-1.5 py-1.5"
        >
          <Plus size={13} />
          Add ticker
        </button>
        <button
          type="button"
          onClick={runScenario}
          disabled={loading || !valid}
          className="btn-primary text-xs gap-1.5 py-1.5"
        >
          {loading
            ? <><RefreshCw size={13} className="animate-spin" /> Calculating…</>
            : 'Run Scenario'
          }
        </button>
      </div>

      {error && <p className="text-xs text-rose-light">{error}</p>}

      {/* Results */}
      {result && (
        <div className="space-y-4 pt-2 border-t border-border">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-surface-DEFAULT rounded-lg p-3">
              <p className="text-xs text-text-muted mb-1">Portfolio Before</p>
              <p className="text-lg font-bold text-text-primary font-mono">{fmt(result.total_before)}</p>
            </div>
            <div className="bg-surface-DEFAULT rounded-lg p-3">
              <p className="text-xs text-text-muted mb-1">Portfolio After</p>
              <p className={`text-lg font-bold font-mono ${netPos ? 'text-emerald' : 'text-rose-light'}`}>
                {fmt(result.total_after)}
              </p>
            </div>
            <div className="bg-surface-DEFAULT rounded-lg p-3 col-span-2 sm:col-span-1">
              <p className="text-xs text-text-muted mb-1">Net Impact</p>
              <p className={`text-lg font-bold font-mono ${netPos ? 'text-emerald' : 'text-rose-light'}`}>
                {netPos ? '+' : ''}{fmt(result.net_gain_loss)}
              </p>
              <p className={`text-xs ${netPos ? 'text-emerald' : 'text-rose-light'}`}>
                {netPos ? '+' : ''}{(result.net_change_pct ?? 0).toFixed(2)}%
              </p>
            </div>
          </div>

          {/* Scenarios applied */}
          <div>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
              Scenarios Applied
            </p>
            <div className="flex flex-wrap gap-2">
              {result.scenarios_applied.map((s, i) => (
                <span
                  key={i}
                  className={clsx(
                    'badge text-xs font-mono',
                    !s.matched ? 'badge-amber' : s.change_pct < 0 ? 'badge-red' : 'badge-green',
                  )}
                  title={s.matched ? 'Matched in portfolio' : 'Not found in portfolio'}
                >
                  {s.ticker}: {s.change_pct > 0 ? '+' : ''}{s.change_pct}%
                  {!s.matched && ' ⚠'}
                </span>
              ))}
            </div>
            {result.unmatched_tickers.length > 0 && (
              <p className="text-xs text-amber mt-1.5">
                Not found in portfolio: {result.unmatched_tickers.join(', ')}
              </p>
            )}
          </div>

          {/* Per-position impact */}
          {result.positions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
                Position Impact ({result.positions_affected} positions affected)
              </p>
              <div className="overflow-x-auto table-responsive">
                <table className="w-full text-xs" aria-label="Scenario position impact">
                  <thead>
                    <tr className="border-b border-border text-text-muted">
                      <th className="py-2 px-3 text-left font-medium">Ticker</th>
                      <th className="py-2 px-3 text-left font-medium hidden sm:table-cell">Name</th>
                      <th className="py-2 px-3 text-right font-medium">Change</th>
                      <th className="py-2 px-3 text-right font-medium">Before</th>
                      <th className="py-2 px-3 text-right font-medium">After</th>
                      <th className="py-2 px-3 text-right font-medium">Gain / Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.positions.map((p, i) => {
                      const pos = p.gain_loss >= 0
                      return (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-hover">
                          <td className="py-2 px-3 font-mono font-medium text-text-primary">{p.ticker}</td>
                          <td className="py-2 px-3 text-text-secondary hidden sm:table-cell truncate max-w-[160px]">{p.name}</td>
                          <td className={clsx('py-2 px-3 text-right font-mono', p.change_pct < 0 ? 'text-rose-light' : 'text-emerald')}>
                            {p.change_pct > 0 ? '+' : ''}{p.change_pct}%
                          </td>
                          <td className="py-2 px-3 text-right font-mono text-text-muted">{fmt(p.value_before)}</td>
                          <td className="py-2 px-3 text-right font-mono text-text-primary">{fmt(p.value_after)}</td>
                          <td className={clsx('py-2 px-3 text-right font-mono font-semibold', pos ? 'text-emerald' : 'text-rose-light')}>
                            {pos ? '+' : ''}{fmt(p.gain_loss)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Target Allocation Section
// ─────────────────────────────────────────────────────────────────────────────

const ASSET_CLASSES = ['UK Equity', 'US Equity', 'Fixed Income', 'Crypto', 'Other', 'Cash'] as const

// ── Feasibility badge (per-trade) ─────────────────────────────────────────────

const VERDICT_BADGE: Record<TradeVerdict, { label: string; className: string }> = {
  executable: {
    label: 'executable',
    className: 'bg-emerald/15 text-emerald border border-emerald/30',
  },
  costly: {
    label: 'costly',
    className: 'bg-amber/15 text-amber border border-amber/30',
  },
  impractical: {
    label: 'impractical',
    className: 'bg-rose-light/15 text-rose-light border border-rose-light/30',
  },
}

function FeasibilityBadge({
  verdict,
  reason,
}: {
  verdict?: TradeVerdict
  reason?: string
}) {
  if (verdict == null) return null
  const cfg = VERDICT_BADGE[verdict] ?? VERDICT_BADGE.executable
  return (
    <span
      className={clsx('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap', cfg.className)}
      title={reason ?? verdict}
    >
      {cfg.label}
    </span>
  )
}

// ── Feasibility banner (plan-level) ───────────────────────────────────────────

const OVERALL_BANNER: Record<string, { className: string; prefix: string }> = {
  clear:   { className: 'bg-emerald/10 border border-emerald/30 text-emerald',      prefix: 'Plan: clear' },
  caution: { className: 'bg-amber/10 border border-amber/30 text-amber',            prefix: 'Plan: caution' },
  blocked: { className: 'bg-rose-light/10 border border-rose-light/30 text-rose-light', prefix: 'Plan: blocked' },
}

function FeasibilityBanner({ summary }: { summary: FeasibilitySummary }) {
  const cfg = OVERALL_BANNER[summary.overall] ?? OVERALL_BANNER.clear
  return (
    <div className={clsx('rounded-lg px-3 py-2 text-xs mb-3 flex items-start gap-2', cfg.className)}>
      <span className="font-semibold shrink-0">{cfg.prefix}</span>
      <span className="text-text-secondary">&mdash;</span>
      <span>{summary.note}</span>
    </div>
  )
}

// ── CGT banner and per-trade CGT cell ─────────────────────────────────────────

function CgtBanner({ tax }: { tax: RebalanceTaxSummary }) {
  if (tax.total_cgt > 0) {
    return (
      <div className="rounded-lg px-3 py-2 text-xs mb-3 flex items-start gap-2 bg-amber/10 border border-amber/30">
        <span className="font-semibold text-amber shrink-0">CGT Warning</span>
        <span className="text-text-secondary">&mdash;</span>
        <span className="text-text-secondary">{tax.note}</span>
      </div>
    )
  }
  // Muted/grey styling only when nothing is actually known: unknown-basis
  // sells exist AND no known realised gain was computed. (The note text from
  // the backend is always accurate; this just picks the right tint.)
  if (tax.gains_unknown_count > 0 && (tax.total_realised_gain ?? 0) <= 0) {
    return (
      <div className="rounded-lg px-3 py-2 text-xs mb-3 flex items-start gap-2 bg-surface-hover border border-border">
        <span className="font-semibold text-text-muted shrink-0">CGT</span>
        <span className="text-text-secondary">&mdash;</span>
        <span className="text-text-muted">{tax.note}</span>
      </div>
    )
  }
  // No CGT due
  return (
    <div className="rounded-lg px-3 py-2 text-xs mb-3 flex items-start gap-2 bg-emerald/10 border border-emerald/20">
      <span className="font-semibold text-emerald shrink-0">CGT</span>
      <span className="text-text-secondary">&mdash;</span>
      <span className="text-text-secondary">{tax.note}</span>
    </div>
  )
}

function CgtCell({ est_cgt, realised_gain, cgt_gain_known, action }: {
  est_cgt?: number
  realised_gain?: number | null
  cgt_gain_known?: boolean
  action: 'BUY' | 'SELL'
}) {
  if (action === 'BUY') {
    return <span className="text-text-muted">—</span>
  }
  if (cgt_gain_known === false) {
    return <span className="text-text-muted italic">basis unknown</span>
  }
  if (realised_gain != null && realised_gain <= 0) {
    return <span className="text-emerald text-xs">Loss</span>
  }
  const cgt = est_cgt ?? 0
  if (cgt <= 0) {
    return <span className="text-emerald text-xs">£0 (within allowance)</span>
  }
  return (
    <span className="text-amber font-semibold">
      {fmt(cgt)}
    </span>
  )
}

// ── Target Allocation section ─────────────────────────────────────────────────

function TargetAllocationSection() {
  const [models, setModels] = useState<ModelPortfolio[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  // Sliders: each asset class → target %
  const [sliders, setSliders] = useState<Record<string, number>>({
    'UK Equity':    25,
    'US Equity':    30,
    'Fixed Income': 15,
    'Crypto':       15,
    'Other':        10,
    'Cash':          5,
  })
  const [result, setResult] = useState<TargetAllocationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load model portfolios once
  useEffect(() => {
    getModelPortfolios().then(r => setModels(r.models)).catch(() => {})
  }, [])

  const total = Object.values(sliders).reduce((a, b) => a + b, 0)
  const isValid = Math.abs(total - 100) < 0.5

  const applyModel = (m: ModelPortfolio) => {
    setSelected(m.name)
    setSliders({ ...sliders, ...m.allocation })
    setResult(null)
    setError(null)
  }

  const updateSlider = (cls: string, val: number) => {
    setSliders(prev => ({ ...prev, [cls]: val }))
    setSelected(null)
    setResult(null)
  }

  const calculate = async () => {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const r = await postTargetAllocation(sliders)
      setResult(r)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Target allocation calculation failed')
    } finally {
      setLoading(false)
    }
  }

  // Build donut data arrays
  const currentDonut = result
    ? result.current_allocation.map(b => ({ name: b.label, value: b.current_pct ?? 0 }))
    : []
  const targetDonut = result
    ? result.current_allocation.map(b => ({ name: b.label, value: b.target_pct ?? 0 }))
    : []

  return (
    <div className="card p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Target size={16} className="text-blue shrink-0" />
        <h3 className="panel-title">Target Allocation</h3>
        <span className="text-xs text-text-muted ml-auto">Set desired asset-class weights and calculate trades needed</span>
      </div>

      {/* Model preset buttons */}
      {models.length > 0 && (
        <div>
          <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Model Portfolios</p>
          <div className="flex flex-wrap gap-2">
            {models.map(m => (
              <button
                key={m.name}
                type="button"
                onClick={() => applyModel(m)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  selected === m.name
                    ? 'bg-blue/20 border-blue/50 text-blue'
                    : 'bg-surface-DEFAULT border-border text-text-secondary hover:text-text-primary hover:border-blue/40',
                )}
              >
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sliders */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sliders size={13} className="text-text-muted" />
          <p className="text-xs text-text-muted uppercase tracking-wider">Custom Weights</p>
          <span className={clsx(
            'ml-auto text-xs font-mono font-bold',
            isValid ? 'text-emerald' : 'text-amber',
          )}>
            {total.toFixed(0)}% {isValid ? '✓' : `(needs ${100 - total > 0 ? '+' : ''}${(100 - total).toFixed(0)}%)`}
          </span>
        </div>
        <div className="space-y-3">
          {ASSET_CLASSES.map(cls => (
            <div key={cls} className="flex items-center gap-3">
              <span className="text-xs text-text-secondary w-28 shrink-0">{cls}</span>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={sliders[cls] ?? 0}
                onChange={e => updateSlider(cls, Number(e.target.value))}
                className="flex-1 accent-blue"
                aria-label={`${cls} target weight`}
              />
              <span className="text-xs font-mono text-text-primary w-10 text-right">
                {(sliders[cls] ?? 0).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Calculate button */}
      <button
        onClick={calculate}
        disabled={loading || !isValid}
        className="btn-primary px-6 py-2 text-sm w-full sm:w-auto"
      >
        {loading ? (
          <span className="flex items-center gap-2 justify-center">
            <RefreshCw size={14} className="animate-spin" /> Calculating trades...
          </span>
        ) : (
          'Calculate Trades'
        )}
      </button>

      {error && <p className="text-xs text-rose-light">{error}</p>}

      {/* Results */}
      {result && (
        <div className="space-y-5 pt-2 border-t border-border">
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-surface-DEFAULT rounded-lg p-3">
              <p className="text-xs text-text-muted">Portfolio Value</p>
              <p className="text-lg font-bold text-text-primary mt-0.5">{fmt(result.total_portfolio_value)}</p>
            </div>
            <div className="bg-surface-DEFAULT rounded-lg p-3">
              <p className="text-xs text-text-muted">Trades Needed</p>
              <p className="text-lg font-bold text-text-primary mt-0.5">{result.trade_count}</p>
            </div>
            <div className="bg-surface-DEFAULT rounded-lg p-3">
              <p className="text-xs text-text-muted">Total Turnover</p>
              <p className="text-lg font-bold text-amber mt-0.5">{fmt(result.total_turnover)}</p>
            </div>
            <div className="bg-surface-DEFAULT rounded-lg p-3">
              <p className="text-xs text-text-muted">Est. Execution Cost</p>
              <p className="text-lg font-bold text-rose-light mt-0.5">
                {fmt(result.total_execution_cost ?? result.total_transaction_cost)}
              </p>
              {(result.execution_cost_pct_of_traded ?? 0) > 0 && (
                <p className="text-xs text-text-muted mt-0.5">
                  {(result.execution_cost_pct_of_traded ?? 0).toFixed(2)}% of traded value
                </p>
              )}
            </div>
          </div>

          {/* Before / After donut charts side by side */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="card p-4">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Current Allocation</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={currentDonut} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                    dataKey="value" nameKey="name">
                    {currentDonut.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                {currentDonut.map((d, i) => (
                  <span key={d.name} className="flex items-center gap-1 text-xs text-text-muted">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    {d.name} {d.value.toFixed(1)}%
                  </span>
                ))}
              </div>
            </div>
            <div className="card p-4">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Target Allocation</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={targetDonut} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                    dataKey="value" nameKey="name">
                    {targetDonut.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                {targetDonut.map((d, i) => (
                  <span key={d.name} className="flex items-center gap-1 text-xs text-text-muted">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    {d.name} {d.value.toFixed(1)}%
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Trade list */}
          {result.trades.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Trades Required</p>

              {/* Plan-level feasibility banner */}
              {result.feasibility_summary != null && (
                <FeasibilityBanner summary={result.feasibility_summary} />
              )}

              {/* Plan-level CGT banner */}
              {result.tax_summary != null && (
                <CgtBanner tax={result.tax_summary} />
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-xs" aria-label="Target allocation trades">
                  <thead>
                    <tr className="border-b border-border text-text-muted">
                      <th className="py-2 px-3 text-left font-medium">Asset Class</th>
                      <th className="py-2 px-3 text-left font-medium hidden sm:table-cell">Instrument</th>
                      <th className="py-2 px-3 text-right font-medium">Current</th>
                      <th className="py-2 px-3 text-right font-medium">Target</th>
                      <th className="py-2 px-3 text-right font-medium">Action</th>
                      <th className="py-2 px-3 text-right font-medium">Value</th>
                      <th className="py-2 px-3 text-right font-medium hidden sm:table-cell">Cost (bps)</th>
                      <th className="py-2 px-3 text-right font-medium">Est. CGT</th>
                      <th className="py-2 px-3 text-right font-medium hidden sm:table-cell">Feasibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades.map((t, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-hover">
                        <td className="py-2 px-3 text-text-primary font-medium">{t.asset_class}</td>
                        <td className="py-2 px-3 text-text-secondary font-mono hidden sm:table-cell">{t.ticker}</td>
                        <td className="py-2 px-3 text-right font-mono text-text-muted">{(t.current_pct ?? 0).toFixed(1)}%</td>
                        <td className="py-2 px-3 text-right font-mono text-text-secondary">{(t.target_pct ?? 0).toFixed(1)}%</td>
                        <td className="py-2 px-3 text-right">
                          <span className={clsx(
                            'font-bold text-xs',
                            t.action === 'SELL' ? 'text-rose-light' : 'text-emerald',
                          )}>
                            {t.action}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-text-primary">{fmt(t.trade_value)}</td>
                        <td className="py-2 px-3 text-right font-mono text-text-muted hidden sm:table-cell" title={`Est. execution cost ${fmt(t.est_execution_cost ?? t.est_transaction_cost ?? 0)}`}>
                          {(t.est_cost_bps ?? 0).toFixed(1)}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <CgtCell
                            action={t.action}
                            est_cgt={t.est_cgt}
                            realised_gain={t.realised_gain}
                            cgt_gain_known={t.cgt_gain_known}
                          />
                        </td>
                        <td className="py-2 px-3 text-right hidden sm:table-cell">
                          <FeasibilityBadge
                            verdict={t.feasibility}
                            reason={t.feasibility_reason}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-text-muted mt-2 italic">{result.note}</p>
            </div>
          )}

          {result.trades.length === 0 && (
            <p className="text-sm text-emerald text-center py-3">
              Portfolio is already within 0.5% of all target weights — no trades needed.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Tax Tab
// ─────────────────────────────────────────────────────────────────────────────

const WRAPPER_COLORS: Record<string, string> = {
  'ISA': '#10b981',
  'SIPP': '#3b82f6',
  'GIA': '#f59e0b',
  'Crypto': '#8b5cf6',
  'Premium Bonds': '#06b6d4',
}

type TaxSubTab = 'wrappers' | 'fees' | 'allowances' | 'cgt-plan'

const TAX_SUB_TABS: { key: TaxSubTab; label: string }[] = [
  { key: 'wrappers',   label: 'Wrappers & CGT' },
  { key: 'fees',       label: 'Fee Analysis' },
  { key: 'allowances', label: 'Allowances' },
  { key: 'cgt-plan',   label: 'CGT Planner' },
]

const BED_ISA_COLORS: Record<string, string> = {
  'Strong candidate':                    'text-emerald',
  'Consider':                            'text-amber',
  'Low priority':                        'text-text-muted',
  'Not recommended':                     'text-rose-light',
  'Value exceeds annual ISA allowance':  'text-blue',
}

function TaxTab() {
  const [subTab, setSubTab] = useState<TaxSubTab>('wrappers')
  const [summary, setSummary] = useState<TaxWrapperSummary | null>(null)
  const [cgt, setCgt] = useState<CgtEstimate | null>(null)
  const [allowances, setAllowances] = useState<TaxAllowances | null>(null)
  const [fees, setFees] = useState<FeeSummary | null>(null)
  const [ter, setTer] = useState<FeeTerAnalysis | null>(null)
  const [allowanceStatus, setAllowanceStatus] = useState<AllowanceStatus | null>(null)
  const [cgtPlan, setCgtPlan] = useState<CgtPlanner | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getTaxWrapperSummary().then(setSummary).catch(() => {}),
      getTaxCgtEstimate().then(setCgt).catch(() => {}),
      getTaxAllowances().then(setAllowances).catch(() => {}),
      getFeeSummary().then(setFees).catch(() => {}),
      getFeeTerAnalysis().then(setTer).catch(() => {}),
      getAllowanceStatus().then(setAllowanceStatus).catch(() => {}),
      getCgtPlanner().then(setCgtPlan).catch(() => {}),
    ]).catch(() => setError('Failed to load tax data')).finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="py-12 text-center text-text-muted text-sm">
        <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue" />
        Loading tax analysis...
      </div>
    )
  }

  if (error) {
    return <div className="card p-4 text-rose-light text-sm">{error}</div>
  }

  const wrapperPieData = summary
    ? summary.wrappers.map(w => ({
        label: w.wrapper,
        value: w.value,
        pct: w.pct,
        color: WRAPPER_COLORS[w.wrapper] ?? '#94a3b8',
      }))
    : []

  return (
    <div className="space-y-5">
      {/* Sub-tab bar */}
      <div className="flex gap-1 flex-wrap">
        {TAX_SUB_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setSubTab(t.key)}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              subTab === t.key
                ? 'bg-blue text-white'
                : 'bg-surface-hover text-text-muted hover:text-text-secondary',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Wrappers & CGT ─────────────────────────────────────────────────── */}
      {subTab === 'wrappers' && (
        <>
      {/* Donut + wrapper cards */}
      {summary && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Donut */}
          <div className="card p-5">
            <h3 className="panel-title mb-3">Portfolio by Tax Wrapper</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={wrapperPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={82}
                  dataKey="value"
                  nameKey="label"
                  paddingAngle={2}
                >
                  {wrapperPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="transparent" />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number, _: string, props: any) => [
                    `${fmt(v)} (${props.payload.pct}%)`,
                    props.payload.label,
                  ]}
                  contentStyle={TOOLTIP_STYLE}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {wrapperPieData.map((d, i) => (
                <span key={i} className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                  {d.label} ({d.pct}%)
                </span>
              ))}
            </div>
          </div>

          {/* Wrapper cards */}
          <div className="space-y-2">
            {summary.wrappers.map(w => (
              <div key={w.wrapper} className="card p-3.5 flex items-start gap-3">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0 mt-1"
                  style={{ backgroundColor: WRAPPER_COLORS[w.wrapper] ?? '#94a3b8' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-text-primary">{w.wrapper}</span>
                    <span className="text-sm font-mono font-bold text-text-primary shrink-0">{fmt(w.value)}</span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{w.description}</p>
                  <p className="text-xs text-blue mt-1">{w.allowance_label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CGT Exposure */}
      {cgt && (
        <div className="card p-5">
          <h3 className="panel-title mb-4">CGT Exposure — GIA &amp; Crypto</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div>
              <p className="text-xs text-text-muted">Unrealised Gains</p>
              <p className={clsx('text-lg font-bold mt-0.5', cgt.total_unrealised_gain >= 0 ? 'text-emerald' : 'text-rose-light')}>
                {cgt.total_unrealised_gain >= 0 ? '+' : ''}{fmt(cgt.total_unrealised_gain)}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">CGT Allowance</p>
              <p className="text-lg font-bold text-text-primary mt-0.5">{fmt(cgt.cgt_allowance)}</p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Taxable Gain</p>
              <p className={clsx('text-lg font-bold mt-0.5', cgt.taxable_gain_after_allowance > 0 ? 'text-amber' : 'text-emerald')}>
                {fmt(cgt.taxable_gain_after_allowance)}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Tax ({cgt.cgt_rate_basic}% / {cgt.cgt_rate_higher}%)</p>
              <p className="text-base font-bold text-text-primary mt-0.5">
                {fmt(cgt.estimated_tax_basic_rate)} / {fmt(cgt.estimated_tax_higher_rate)}
              </p>
            </div>
          </div>

          <p className="text-xs text-text-muted mb-4 italic">{cgt.note}</p>

          <div className="space-y-3">
            {cgt.accounts.map(acc => (
              <div key={acc.account_id}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-text-secondary">{acc.account_name}</span>
                  <span className={clsx('text-xs font-mono font-bold', acc.unrealised_gain >= 0 ? 'text-emerald' : 'text-rose-light')}>
                    {acc.unrealised_gain >= 0 ? '+' : ''}{fmt(acc.unrealised_gain)}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" aria-label={`CGT detail for ${acc.account_name}`}>
                    <thead>
                      <tr className="border-b border-border text-text-muted uppercase tracking-wider">
                        <th className="py-1.5 px-2 text-left font-medium">Asset</th>
                        <th className="py-1.5 px-2 text-right font-medium">Cost</th>
                        <th className="py-1.5 px-2 text-right font-medium">Value</th>
                        <th className="py-1.5 px-2 text-right font-medium">Gain</th>
                        <th className="py-1.5 px-2 text-right font-medium">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acc.holdings.map((h, i) => (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                          <td className="py-1.5 px-2 text-text-primary font-medium">{h.name}</td>
                          <td className="py-1.5 px-2 text-right text-text-secondary font-mono">{fmt(h.cost_basis)}</td>
                          <td className="py-1.5 px-2 text-right text-text-primary font-mono">{fmt(h.current_value)}</td>
                          <td className={clsx('py-1.5 px-2 text-right font-mono font-bold', h.unrealised_gain >= 0 ? 'text-emerald' : 'text-rose-light')}>
                            {h.unrealised_gain >= 0 ? '+' : ''}{fmt(h.unrealised_gain)}
                          </td>
                          <td className={clsx('py-1.5 px-2 text-right font-mono', h.gain_pct >= 0 ? 'text-emerald' : 'text-rose-light')}>
                            {(h.gain_pct ?? 0) >= 0 ? '+' : ''}{(h.gain_pct ?? 0).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Allowances checklist */}
      {allowances && (
        <div className="card p-5">
          <h3 className="panel-title mb-4">
            {allowances.tax_year} Tax Allowances
          </h3>
          <div className="space-y-3">
            {allowances.allowances.map(a => {
              const usedPct = a.annual_limit > 0 ? Math.min(100, (a.used / a.annual_limit) * 100) : 0
              const statusColor = usedPct >= 90 ? 'text-rose-light' : usedPct >= 50 ? 'text-amber' : 'text-emerald'
              return (
                <div key={a.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-sm font-medium text-text-primary">{a.name}</span>
                      <span className="text-xs text-text-muted ml-2">{a.wrapper}</span>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <span className={clsx('text-xs font-mono font-bold', statusColor)}>
                        {fmt(a.remaining)} remaining
                      </span>
                      <span className="text-xs text-text-muted ml-1.5">of {fmt(a.annual_limit)}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-surface-DEFAULT rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full transition-all', usedPct >= 90 ? 'bg-rose-light' : usedPct >= 50 ? 'bg-amber' : 'bg-emerald')}
                      style={{ width: `${usedPct}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">{a.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}
        </>
      )}

      {/* ── Fee Analysis ──────────────────────────────────────────────────── */}
      {subTab === 'fees' && (
        <div className="space-y-4">
          {fees && (
            <div className="card p-5">
              <h3 className="panel-title mb-4">Annual Fee Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <div>
                  <p className="text-xs text-text-muted">Platform Fees</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5">£{(fees.total_platform_fees_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}/yr</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Fund TER Drag</p>
                  <p className="text-lg font-bold text-amber mt-0.5">£{(fees.total_ter_cost_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}/yr</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Total Annual Cost</p>
                  <p className="text-lg font-bold text-rose-light mt-0.5">£{(fees.total_annual_fees_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}/yr</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Portfolio Drag</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5">{(fees.portfolio_drag_pct ?? 0).toFixed(3)}%/yr</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" aria-label="Fee breakdown by account">
                  <thead>
                    <tr className="border-b border-border text-text-muted uppercase tracking-wider">
                      <th className="py-2 px-3 text-left font-medium">Account</th>
                      <th className="py-2 px-3 text-right font-medium">Value</th>
                      <th className="py-2 px-3 text-right font-medium">Platform Fee</th>
                      <th className="py-2 px-3 text-right font-medium">TER Cost</th>
                      <th className="py-2 px-3 text-right font-medium">Total/yr</th>
                      <th className="py-2 px-3 text-right font-medium hidden md:table-cell">Eff. Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.accounts.map(a => (
                      <tr key={a.account_id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                        <td className="py-2 px-3">
                          <span className="text-text-primary font-medium">{a.account_name}</span>
                          {a.platform_fee_capped && (
                            <span className="ml-1.5 badge badge-blue text-xs">capped</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right text-text-secondary font-mono">£{(a.value ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                        <td className="py-2 px-3 text-right font-mono text-text-primary">£{(a.platform_fee_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                        <td className="py-2 px-3 text-right font-mono text-amber">£{(a.ter_cost_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-text-primary">£{(a.total_annual_fee_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                        <td className="py-2 px-3 text-right font-mono text-text-muted hidden md:table-cell">{(a.effective_fee_pct ?? 0).toFixed(3)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-text-muted mt-3 italic">{fees.note}</p>
            </div>
          )}

          {ter && ter.funds.length > 0 && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="panel-title">TER Analysis — Fund Costs</h3>
                <div className="text-right">
                  <p className="text-xs text-text-muted">Weighted Avg TER</p>
                  <p className="text-base font-bold text-amber">{(ter.weighted_average_ter_pct ?? 0).toFixed(3)}%</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" aria-label="TER by fund">
                  <thead>
                    <tr className="border-b border-border text-text-muted uppercase tracking-wider">
                      <th className="py-2 px-3 text-left font-medium">Fund</th>
                      <th className="py-2 px-3 text-left font-medium hidden md:table-cell">Account</th>
                      <th className="py-2 px-3 text-right font-medium">Value</th>
                      <th className="py-2 px-3 text-right font-medium">TER</th>
                      <th className="py-2 px-3 text-right font-medium">Annual Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ter.funds.slice(0, 20).map(f => (
                      <tr key={f.holding_id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                        <td className="py-2 px-3 text-text-primary font-medium max-w-[180px] truncate">{f.name}</td>
                        <td className="py-2 px-3 text-text-muted hidden md:table-cell truncate max-w-[140px]">{f.account_name}</td>
                        <td className="py-2 px-3 text-right font-mono text-text-secondary">£{f.value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                        <td className="py-2 px-3 text-right font-mono text-amber">{f.ter_pct.toFixed(2)}%</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-text-primary">£{f.annual_cost_gbp.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-text-muted mt-2 italic">{ter.note}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Allowance Tracker ─────────────────────────────────────────────── */}
      {subTab === 'allowances' && allowanceStatus && (
        <div className="space-y-4">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="panel-title">{allowanceStatus.tax_year} Tax Allowances</h3>
              <span className="text-xs text-text-muted">{allowanceStatus.tax_year_start} – {allowanceStatus.tax_year_end}</span>
            </div>
            <div className="space-y-4">
              {allowanceStatus.allowances.map(a => {
                const barColor = a.status === 'full' ? 'bg-rose-light' : a.status === 'warning' ? 'bg-amber' : 'bg-emerald'
                const textColor = a.status === 'full' ? 'text-rose-light' : a.status === 'warning' ? 'text-amber' : 'text-emerald'
                return (
                  <div key={a.key} className="space-y-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="text-sm font-semibold text-text-primary">{a.name}</span>
                        <span className="text-xs text-text-muted ml-2 badge">{a.wrapper}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={clsx('text-sm font-mono font-bold', textColor)}>
                          £{a.remaining.toLocaleString('en-GB', { maximumFractionDigits: 0 })} left
                        </span>
                        <span className="text-xs text-text-muted ml-1.5">of £{a.annual_limit.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-surface-DEFAULT rounded-full overflow-hidden">
                      <div className={clsx('h-full rounded-full transition-all', barColor)} style={{ width: `${a.used_pct}%` }} />
                    </div>
                    <p className="text-xs text-text-muted">{a.description}</p>
                    <p className="text-xs text-blue italic">{a.tip}</p>
                    {a.current_isa_portfolio_value !== undefined && (
                      <p className="text-xs text-text-muted">Current ISA portfolio: £{a.current_isa_portfolio_value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</p>
                    )}
                    {a.current_sipp_portfolio_value !== undefined && (
                      <p className="text-xs text-text-muted">Current SIPP portfolio: £{a.current_sipp_portfolio_value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</p>
                    )}
                  </div>
                )
              })}
            </div>
            <p className="text-xs text-text-muted mt-4 italic border-t border-border pt-3">{allowanceStatus.note}</p>
          </div>
        </div>
      )}

      {/* ── CGT Planner ───────────────────────────────────────────────────── */}
      {subTab === 'cgt-plan' && cgtPlan && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="panel-title">CGT Planner &amp; Bed-and-ISA</h3>
              <button
                type="button"
                onClick={() => downloadCsv(
                  '/api/tax/cgt-planner.csv',
                  `cgt-planner-${(cgtPlan.tax_year || '').replace('/', '-')}.csv`,
                )}
                className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary"
                title="Export the bed-and-ISA plan as CSV"
              >
                <Download size={13} /> Export CSV
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-text-muted">Unrealised Gain (GIA/Crypto)</p>
                <p className={clsx('text-lg font-bold mt-0.5', cgtPlan.total_unrealised_gain >= 0 ? 'text-emerald' : 'text-rose-light')}>
                  {cgtPlan.total_unrealised_gain >= 0 ? '+' : ''}£{Math.abs(cgtPlan.total_unrealised_gain).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-muted">CGT Allowance</p>
                <p className="text-lg font-bold text-text-primary mt-0.5">£{cgtPlan.cgt_allowance.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</p>
              </div>
              <div>
                <p className="text-xs text-text-muted">
                  Tax if Sold{cgtPlan.applicable_cgt_rate_pct ? ` (${cgtPlan.applicable_cgt_rate_pct}%)` : ' (18% / 24%)'}
                </p>
                <p className="text-base font-bold text-text-primary mt-0.5">
                  {cgtPlan.total_tax_if_sold_applicable != null
                    ? `£${cgtPlan.total_tax_if_sold_applicable.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
                    : `£${cgtPlan.total_tax_if_sold_basic.toLocaleString('en-GB', { maximumFractionDigits: 0 })} / £${cgtPlan.total_tax_if_sold_higher.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`}
                </p>
                {cgtPlan.total_tax_if_sold_applicable != null && (
                  <p className="text-[10px] text-text-muted mt-0.5">
                    range £{cgtPlan.total_tax_if_sold_basic.toLocaleString('en-GB', { maximumFractionDigits: 0 })}–£{cgtPlan.total_tax_if_sold_higher.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-text-muted">Bed+ISA Candidates</p>
                <p className="text-lg font-bold text-blue mt-0.5">{cgtPlan.bed_isa_top_candidates}</p>
              </div>
            </div>
            <div className="bg-blue/10 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue leading-relaxed">{cgtPlan.note}</p>
            </div>
            <p className="text-xs text-text-secondary leading-relaxed">{cgtPlan.summary}</p>
          </div>

          {/* Holdings table */}
          <div className="card p-5">
            <h3 className="panel-title mb-4">Holdings — Sorted by Bed-and-ISA Priority</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" aria-label="CGT planner holdings">
                <thead>
                  <tr className="border-b border-border text-text-muted uppercase tracking-wider">
                    <th className="py-2 px-3 text-left font-medium">Asset</th>
                    <th className="py-2 px-3 text-left font-medium hidden md:table-cell">Account</th>
                    <th className="py-2 px-3 text-right font-medium">Value</th>
                    <th className="py-2 px-3 text-right font-medium">Gain</th>
                    <th className="py-2 px-3 text-right font-medium hidden sm:table-cell">Tax (Higher)</th>
                    <th className="py-2 px-3 text-right font-medium">Score</th>
                    <th className="py-2 px-3 text-left font-medium hidden lg:table-cell">Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {cgtPlan.holdings.map(h => (
                    <tr key={h.holding_id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                      <td className="py-2 px-3">
                        <span className="text-text-primary font-medium">{h.name}</span>
                        <span className="text-text-muted ml-1.5 hidden sm:inline">{h.wrapper}</span>
                      </td>
                      <td className="py-2 px-3 text-text-muted hidden md:table-cell truncate max-w-[120px]">{h.account_name}</td>
                      <td className="py-2 px-3 text-right font-mono text-text-secondary">£{h.current_value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                      <td className={clsx('py-2 px-3 text-right font-mono font-bold', h.unrealised_gain >= 0 ? 'text-emerald' : 'text-rose-light')}>
                        {h.unrealised_gain >= 0 ? '+' : ''}£{Math.abs(h.unrealised_gain).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                        <span className="text-text-muted font-normal ml-1">({(h.gain_pct ?? 0) >= 0 ? '+' : ''}{(h.gain_pct ?? 0).toFixed(1)}%)</span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-rose-light hidden sm:table-cell">£{(h.tax_if_sold_applicable ?? h.tax_if_sold_higher).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</td>
                      <td className="py-2 px-3 text-right">
                        <span className={clsx('font-mono font-bold text-sm', h.bed_isa_score >= 60 ? 'text-emerald' : h.bed_isa_score >= 30 ? 'text-amber' : 'text-text-muted')}>
                          {h.bed_isa_score.toFixed(0)}
                        </span>
                      </td>
                      <td className="py-2 px-3 hidden lg:table-cell">
                        <span className={clsx('text-xs font-medium', BED_ISA_COLORS[h.bed_isa_recommendation] ?? 'text-text-muted')}>
                          {h.bed_isa_recommendation}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Strategy Tab — personalised rules-based recommendations
// ─────────────────────────────────────────────────────────────────────────────

const PRIORITY_LABELS: Record<number, { label: string; badgeClass: string }> = {
  1: { label: 'Urgent',  badgeClass: 'badge-red' },
  2: { label: 'High',    badgeClass: 'bg-orange-900/40 text-orange-300 badge' },
  3: { label: 'Medium',  badgeClass: 'badge-amber' },
  4: { label: 'Low',     badgeClass: 'badge-blue' },
  5: { label: 'Info',    badgeClass: 'badge' },
}

const ACTION_COLORS: Record<string, string> = {
  SELL:   'badge-red',
  BUY:    'badge-green',
  REVIEW: 'badge-amber',
}

const CATEGORY_LABELS: Record<string, string> = {
  concentration:   'Concentration',
  diversification: 'Diversification',
  profit_taking:   'Profit Taking',
  income:          'Income',
  risk:            'Risk',
  rebalancing:     'Rebalancing',
  opportunity:     'Opportunity',
}

const GRADE_COLORS: Record<string, string> = {
  A: 'text-emerald',
  B: 'text-blue',
  C: 'text-amber',
  D: 'text-orange-400',
  F: 'text-rose-light',
}

function HealthScoreCard({ health }: { health: PortfolioHealthResponse }) {
  const gradeColor = GRADE_COLORS[health.grade] ?? 'text-text-primary'
  const scoreColor = health.score >= 70 ? '#10b981' : health.score >= 50 ? '#f59e0b' : '#f43f5e'

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">Portfolio Health Score</h3>
        <div className="flex items-center gap-2">
          <span className={`text-3xl font-bold ${gradeColor}`}>{health.grade}</span>
          <span className="text-xl font-bold text-text-primary">{health.score}<span className="text-sm text-text-muted">/100</span></span>
        </div>
      </div>

      {/* Score bar */}
      <div className="h-3 bg-surface-hover rounded-full overflow-hidden mb-4">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${health.score}%`, backgroundColor: scoreColor }}
        />
      </div>

      <p className="text-xs text-text-secondary mb-4 leading-relaxed">{health.summary}</p>

      {/* Sub-score breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(health.breakdown).map(([dim, info]) => {
          const pct = (info.score / info.max) * 100
          const color = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#f43f5e'
          return (
            <div key={dim} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-text-secondary capitalize">{dim.replace(/_/g, ' ')}</span>
                <span className="font-mono text-text-muted">{info.score}/{info.max}</span>
              </div>
              <div className="h-1.5 bg-surface-hover rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
              <p className="text-xs text-text-muted leading-tight">{info.note}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function RecommendationCard({ rec }: { rec: StrategyRecommendation }) {
  const pri = PRIORITY_LABELS[rec.priority] ?? PRIORITY_LABELS[5]
  const actionClass = ACTION_COLORS[rec.action] ?? 'badge'
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="card p-4 space-y-3">
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 shrink-0 mt-0.5">
          <span className={`badge text-xs ${pri.badgeClass}`}>{pri.label}</span>
          <span className={`badge text-xs ${actionClass}`}>{rec.action}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary leading-tight">{rec.title}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {rec.ticker && (
              <span className="text-xs font-mono text-blue-light">{rec.ticker}</span>
            )}
            <span className="text-xs text-text-muted">{rec.account}</span>
            <span className="text-xs badge bg-surface-hover text-text-muted">{CATEGORY_LABELS[rec.category] ?? rec.category}</span>
          </div>
        </div>
      </div>

      {/* Weight bar */}
      {rec.current_weight !== null && rec.target_weight !== null && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-text-muted">
            <span>Current {rec.current_weight}%</span>
            <span>Target {rec.target_weight}%</span>
          </div>
          <div className="h-2 bg-surface-hover rounded-full overflow-hidden relative">
            <div
              className="h-full rounded-full bg-rose-700 transition-all"
              style={{ width: `${Math.min(rec.current_weight, 100)}%` }}
            />
            <div
              className="absolute top-0 h-full border-l-2 border-emerald"
              style={{ left: `${Math.min(rec.target_weight, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Trade detail */}
      {(rec.suggested_quantity !== null || rec.estimated_value) && (
        <div className="flex gap-4 text-xs">
          {rec.suggested_quantity !== null && (
            <div>
              <span className="text-text-muted">Suggested qty</span>
              <span className="ml-1.5 font-mono text-text-primary">{rec.suggested_quantity}</span>
            </div>
          )}
          {rec.estimated_value && (
            <div>
              <span className="text-text-muted">Est. value</span>
              <span className="ml-1.5 font-mono text-text-primary">{rec.estimated_value}</span>
            </div>
          )}
        </div>
      )}

      {/* Reasoning (expandable) */}
      <div>
        <button
          className="text-xs text-blue-light hover:underline flex items-center gap-1"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Less' : 'Why?'}
        </button>
        {expanded && (
          <div className="mt-2 space-y-1.5">
            <p className="text-xs text-text-secondary leading-relaxed">{rec.reasoning}</p>
            {rec.risk_if_ignored && (
              <div className="flex items-start gap-1.5 mt-2 p-2 rounded bg-rose-900/10 border border-rose-800/30">
                <AlertTriangle size={11} className="text-rose-400 shrink-0 mt-0.5" />
                <p className="text-xs text-rose-300">{rec.risk_if_ignored}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Model Comparison sub-components
// ─────────────────────────────────────────────────────────────────────────────

const PERSONA_COLORS: Record<string, string> = {
  buffett: '#16a34a',
  dalio:   '#2563eb',
  soros:   '#d97706',
  simons:  '#7c3aed',
  cathie:  '#db2777',
  burry:   '#dc2626',
}

const PERSONA_IDS = ['buffett', 'dalio', 'burry', 'soros', 'simons', 'cathie'] as const
type PersonaId = typeof PERSONA_IDS[number]

function RiskBar({ score }: { score: number }) {
  const color = score >= 8 ? '#dc2626' : score >= 6 ? '#f59e0b' : '#16a34a'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-surface-hover rounded-full h-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${score * 10}%`, background: color }}
        />
      </div>
      <span className="text-xs font-mono text-text-secondary w-8">{score}/10</span>
    </div>
  )
}

function GapBadge({ gap }: { gap: number }) {
  if (Math.abs(gap) < 1) return <span className="text-xs text-text-muted">≈ Aligned</span>
  const isOver = gap > 0
  return (
    <span className={clsx(
      'text-xs font-medium px-1.5 py-0.5 rounded',
      isOver ? 'bg-amber-500/15 text-amber-400' : 'bg-blue/15 text-blue-400',
    )}>
      {isOver ? '+' : ''}{gap.toFixed(0)}pp {isOver ? 'OVW' : 'UNW'}
    </span>
  )
}

function ModelComparisonPanel() {
  const [data, setData] = useState<ModelComparisonResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedModel, setSelectedModel] = useState(0)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await getPortfolioCompareModels()
      setData(r)
      setLoaded(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to load comparison')
    } finally {
      setLoading(false)
    }
  }, [])

  // Lazy load on first expand
  if (!loaded && !loading) {
    return (
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-text-primary">Portfolio vs Model Portfolios</h3>
          <button type="button" onClick={load} className="btn-ghost text-xs gap-1.5 flex items-center">
            <TrendingUp size={13} />
            Load comparison
          </button>
        </div>
        <p className="text-xs text-text-muted">
          Compare your allocation to 60/40, Vanguard LifeStrategy 80, All-Weather (Dalio), and Aggressive Growth.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="card p-5 text-center text-sm text-text-muted">
        <RefreshCw size={16} className="animate-spin mx-auto mb-2 text-blue" />
        Loading model comparison…
      </div>
    )
  }

  if (error || !data) {
    return <div className="card p-4 text-rose-light text-sm">{error ?? 'No data'}</div>
  }

  const model = data.models[selectedModel]
  if (!model) return null

  // Build radar chart data: merge actual + model axes
  const radarData = model.actual_radar.map((pt, i) => ({
    axis: pt.axis,
    actual: pt.value,
    model: model.model_radar[i]?.value ?? 0,
  }))

  return (
    <div className="card p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-text-primary">Portfolio vs Model Portfolios</h3>
        <button onClick={load} disabled={loading} className="btn-ghost p-1.5" aria-label="Refresh">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary headline */}
      <div className="bg-surface-hover rounded-lg p-3 text-xs text-text-secondary">
        {data.summary.headline}
      </div>

      {/* Model selector */}
      <div className="flex gap-2 flex-wrap">
        {data.models.map((m, i) => (
          <button
            type="button"
            key={m.id}
            onClick={() => setSelectedModel(i)}
            className={clsx(
              'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
              selectedModel === i
                ? 'bg-blue text-white'
                : 'bg-surface-hover text-text-muted hover:text-text-secondary',
            )}
          >
            {m.name}
            <span className="ml-1.5 opacity-70">{m.match_score.toFixed(0)}% match</span>
          </button>
        ))}
      </div>

      {/* Two-column layout: radar + gap table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar chart */}
        <div>
          <p className="text-xs text-text-muted mb-2">Allocation radar (You vs {model.name})</p>
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#334155" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <PolarRadiusAxis angle={90} domain={[0, 50]} tick={{ fontSize: 9, fill: '#64748b' }} />
              <Radar name="Your portfolio" dataKey="actual" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} />
              <Radar name={model.name} dataKey="model" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => `${v.toFixed(0)}%`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Gap table */}
        <div>
          <p className="text-xs text-text-muted mb-2">Gap analysis vs {model.name}</p>
          <div className="space-y-1.5">
            {model.gap_analysis.filter(g => g.severity !== 'ok').map(g => (
              <div key={g.asset_class} className="flex items-center justify-between text-xs">
                <span className="text-text-secondary w-28">{g.asset_class}</span>
                <div className="flex items-center gap-3 text-right">
                  <span className="text-text-muted w-12">You: {g.actual_pct.toFixed(0)}%</span>
                  <span className="text-text-muted w-14">Model: {g.model_pct.toFixed(0)}%</span>
                  <GapBadge gap={g.gap_pct} />
                </div>
              </div>
            ))}
            {model.gap_analysis.filter(g => g.severity === 'ok').length > 0 && (
              <p className="text-xs text-text-muted pt-1">
                {model.gap_analysis.filter(g => g.severity === 'ok').length} asset classes aligned
              </p>
            )}
          </div>

          {/* Recommendations */}
          {model.recommendations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-surface-hover">
              <p className="text-xs font-medium text-text-secondary mb-1.5">Suggested actions:</p>
              <ul className="space-y-1">
                {model.recommendations.slice(0, 4).map((r, i) => (
                  <li key={i} className="text-xs text-text-muted flex gap-1.5">
                    <span className="text-amber-400 shrink-0">›</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Key risks */}
      <div className="border-t border-surface-hover pt-3">
        <p className="text-xs font-medium text-text-secondary mb-1.5">Key portfolio risks:</p>
        <div className="flex flex-wrap gap-2">
          {data.summary.key_risks.map((r, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-rose-500/10 text-rose-light rounded-full">
              {r}
            </span>
          ))}
        </div>
      </div>

      <p className="text-xs text-text-muted text-center pt-1">
        Run <code className="bg-surface-hover px-1 rounded">/compare-models</code> in terminal for full ASCII table
      </p>
    </div>
  )
}

// ── What Would X Do panel ──────────────────────────────────────────────────

function WhatWouldPanel() {
  const [selectedPersona, setSelectedPersona] = useState<PersonaId>('buffett')
  const [results, setResults] = useState<Partial<Record<PersonaId, WhatWouldResponse>>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPersona = useCallback(async (persona: PersonaId) => {
    if (results[persona]) return  // cached
    setLoading(true)
    setError(null)
    try {
      const r = await getPortfolioWhatWould(persona)
      setResults(prev => ({ ...prev, [persona]: r }))
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? `Failed to load ${persona} analysis`)
    } finally {
      setLoading(false)
    }
  }, [results])

  const current = results[selectedPersona]
  const color = PERSONA_COLORS[selectedPersona] ?? '#3b82f6'

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-text-primary">"What Would X Do?"</h3>
        {current && (
          <button
            onClick={() => {
              setResults(prev => { const n = { ...prev }; delete n[selectedPersona]; return n })
              fetchPersona(selectedPersona)
            }}
            className="btn-ghost p-1.5"
            aria-label="Refresh"
          >
            <RefreshCw size={13} />
          </button>
        )}
      </div>

      {/* Persona selector */}
      <div className="flex gap-2 flex-wrap">
        {PERSONA_IDS.map(pid => {
          const active = selectedPersona === pid
          const accentColor = PERSONA_COLORS[pid]
          return (
            <button
              type="button"
              key={pid}
              onClick={() => {
                setSelectedPersona(pid)
                if (!results[pid]) fetchPersona(pid)
              }}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                active
                  ? 'text-white border-transparent'
                  : 'bg-surface-hover border-surface-hover text-text-muted hover:text-text-secondary',
              )}
              {...(active ? { 'data-accent': accentColor } : {})}
              ref={active ? (el) => { if (el) { el.style.background = accentColor; el.style.borderColor = accentColor } } : undefined}
            >
              {pid.charAt(0).toUpperCase() + pid.slice(1)}
              {results[pid] && <span className="ml-1 opacity-70">✓</span>}
            </button>
          )
        })}
      </div>

      {/* Load prompt if not loaded */}
      {!current && !loading && (
        <div className="text-center py-6">
          <button
            onClick={() => fetchPersona(selectedPersona)}
            className="btn-primary text-sm gap-2 flex items-center mx-auto"
          >
            <TrendingUp size={14} />
            Ask {selectedPersona.charAt(0).toUpperCase() + selectedPersona.slice(1)} what they'd change
          </button>
          <p className="text-xs text-text-muted mt-2">Powered by AI · takes ~5s</p>
        </div>
      )}

      {loading && (
        <div className="text-center py-6 text-sm text-text-muted">
          <RefreshCw size={16} className="animate-spin mx-auto mb-2" style={{ color }} />
          Asking {selectedPersona}…
        </div>
      )}

      {error && <div className="text-rose-light text-sm">{error}</div>}

      {current && !loading && (
        <div className="space-y-4">
          {/* Persona header */}
          <div className="flex items-center gap-3 pb-2 border-b border-surface-hover">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{ background: color }}
            >
              {current.persona_initials}
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary">{current.persona_name}</p>
              <p className="text-xs text-text-muted">{current.persona_style}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-text-muted">Risk score</p>
              <div className="w-32 mt-1">
                <RiskBar score={current.risk_score} />
              </div>
              <p className="text-xs text-text-muted mt-0.5">{current.risk_label}</p>
            </div>
          </div>

          {/* Verdict */}
          <div className="bg-surface-hover rounded-lg p-3">
            <p className="text-xs font-medium text-text-secondary mb-1">Verdict</p>
            <p className="text-sm text-text-primary">{current.verdict}</p>
          </div>

          {/* Biggest problem */}
          {current.biggest_problem && (
            <div className="border border-rose-500/30 bg-rose-500/5 rounded-lg p-3">
              <p className="text-xs font-medium text-rose-light mb-1">Biggest Problem</p>
              <p className="text-sm text-text-primary">{current.biggest_problem}</p>
            </div>
          )}

          {/* Two-column: what to change / keep */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {current.what_id_change.length > 0 && (
              <div>
                <p className="text-xs font-medium text-text-secondary mb-1.5">What I'd change</p>
                <ul className="space-y-1.5">
                  {current.what_id_change.slice(0, 4).map((c, i) => (
                    <li key={i} className="text-xs text-text-secondary flex gap-1.5">
                      <span className="text-rose-400 shrink-0 mt-0.5">—</span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {current.what_id_keep.length > 0 && (
              <div>
                <p className="text-xs font-medium text-text-secondary mb-1.5">What I'd keep</p>
                <ul className="space-y-1.5">
                  {current.what_id_keep.slice(0, 3).map((k, i) => (
                    <li key={i} className="text-xs text-text-secondary flex gap-1.5">
                      <span className="text-green-400 shrink-0 mt-0.5">+</span>
                      {k}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Position changes */}
          {(current.reduce_positions.length > 0 || current.add_positions.length > 0) && (
            <div className="border-t border-surface-hover pt-3">
              <p className="text-xs font-medium text-text-secondary mb-2">Position changes</p>
              <div className="flex flex-wrap gap-2">
                {current.reduce_positions.slice(0, 4).map((r, i) => (
                  <div key={i} className="bg-rose-500/10 rounded-lg px-2.5 py-1.5 text-xs">
                    <span className="text-rose-400 font-mono font-bold">{r.ticker}</span>
                    {r.from_pct !== undefined && r.to_pct !== undefined && (
                      <span className="text-text-muted ml-1.5">{r.from_pct.toFixed(0)}% → {r.to_pct.toFixed(0)}%</span>
                    )}
                    <p className="text-text-muted mt-0.5">{r.reason}</p>
                  </div>
                ))}
                {current.add_positions.slice(0, 4).map((a, i) => (
                  <div key={i} className="bg-green-500/10 rounded-lg px-2.5 py-1.5 text-xs">
                    <span className="text-green-400 font-mono font-bold">+ {a.ticker}</span>
                    {a.allocation_pct !== undefined && (
                      <span className="text-text-muted ml-1.5">{a.allocation_pct.toFixed(0)}%</span>
                    )}
                    <p className="text-text-muted mt-0.5">{a.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-text-muted text-center pt-1">
        AI analysis · Run <code className="bg-surface-hover px-1 rounded">/what-would {selectedPersona}</code> in terminal
      </p>
    </div>
  )
}

function StrategyTab() {
  const [recs, setRecs] = useState<StrategyRecommendationsResponse | null>(null)
  const [health, setHealth] = useState<PortfolioHealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, h] = await Promise.all([
        getStrategyRecommendations(),
        getPortfolioHealth(),
      ])
      setRecs(r)
      setHealth(h)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to load strategy recommendations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const categories = recs
    ? ['all', ...Array.from(new Set(recs.recommendations.map(r => r.category)))]
    : ['all']

  const filtered = (recs?.recommendations ?? []).filter(r =>
    filter === 'all' || r.category === filter
  ) ?? []

  if (loading) {
    return (
      <div className="py-12 text-center text-text-muted text-sm">
        <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue" />
        Running strategy analysis…
      </div>
    )
  }

  if (error) {
    return <div className="card p-4 text-rose-light text-sm">{error}</div>
  }

  return (
    <div className="space-y-5">
      {/* Health score + refresh */}
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-text-primary">Personalised Strategy</h2>
        <button onClick={load} className="btn-ghost text-xs gap-1.5 flex items-center">
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      {health && <HealthScoreCard health={health} />}

      {/* Category filter */}
      {recs && recs.count > 0 && (
        <>
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={clsx(
                  'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                  filter === cat
                    ? 'bg-blue text-white'
                    : 'bg-surface-hover text-text-muted hover:text-text-secondary',
                )}
              >
                {cat === 'all' ? `All (${recs.count})` : CATEGORY_LABELS[cat] ?? cat}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {filtered.map(rec => (
              <RecommendationCard key={rec.id} rec={rec} />
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-text-muted py-4 text-center">No recommendations in this category.</p>
            )}
          </div>
        </>
      )}

      {recs && recs.count === 0 && (
        <div className="card p-8 text-center">
          <p className="text-text-secondary text-sm">Your portfolio looks well-balanced — no urgent actions needed.</p>
        </div>
      )}

      <p className="text-xs text-text-muted text-center pt-2">
        Rules-based analysis · No AI · Data from your live accounts · Run <code className="bg-surface-hover px-1 rounded">/strategy recs</code> in terminal
      </p>

      {/* Divider */}
      <div className="border-t border-surface-hover pt-1" />

      {/* Model portfolio comparison */}
      <ModelComparisonPanel />

      {/* What would X do */}
      <WhatWouldPanel />
    </div>
  )
}

// Section tabs
// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'holdings' | 'performance' | 'risk' | 'dividends' | 'benchmark' | 'tax' | 'rebalance' | 'strategy' | 'accounts'

// Lazy tab content re-homed from the former standalone /app/holdings page
const HoldingsTab = lazy(() => import('./HoldingsPage'))

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'holdings', label: 'Holdings' },
  { key: 'performance', label: 'Performance' },
  { key: 'risk', label: 'Risk' },
  { key: 'dividends', label: 'Dividends' },
  { key: 'benchmark', label: 'Benchmark' },
  { key: 'tax', label: 'Tax' },
  { key: 'rebalance', label: 'Rebalance' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'accounts', label: 'Accounts' },
]

// ─────────────────────────────────────────────────────────────────────────────
// Benchmark Tab
// ─────────────────────────────────────────────────────────────────────────────

const ALL_BENCHMARKS = ['SPY', 'QQQ', 'IWM', 'DIA', 'VTI', 'VXUS', 'AGG'] as const
type BenchmarkSymbol = typeof ALL_BENCHMARKS[number]

const BENCHMARK_LABELS: Record<string, string> = {
  SPY: 'S&P 500', QQQ: 'Nasdaq 100', IWM: 'Russell 2000',
  DIA: 'Dow Jones', VTI: 'Total US', VXUS: 'Intl Mkt', AGG: 'US Bonds',
  portfolio: 'Portfolio',
}

const BENCHMARK_COLORS: Record<string, string> = {
  portfolio: '#3b82f6',
  SPY: '#94a3b8',
  QQQ: '#f59e0b',
  IWM: '#10b981',
  DIA: '#8b5cf6',
  VTI: '#06b6d4',
  VXUS: '#f43f5e',
  AGG: '#84cc16',
}

const PERIODS = ['1m', '3m', '6m', '1y', '2y', '3y', '5y'] as const
type Period = typeof PERIODS[number]

function BenchmarkTab() {
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<BenchmarkSymbol[]>(['SPY', 'QQQ', 'IWM'])
  const [period, setPeriod] = useState<Period>('1y')
  const [data, setData] = useState<BenchmarkComparison | null>(null)
  const [factors, setFactors] = useState<FactorExposureResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [benchData, factorData] = await Promise.all([
        getPortfolioBenchmark(selectedBenchmarks.join(','), period),
        getPortfolioFactorExposure(),
      ])
      setData(benchData)
      setFactors(factorData)
    } catch (e) {
      setError('Failed to load benchmark data')
    } finally {
      setLoading(false)
    }
  }, [selectedBenchmarks, period]) // eslint-disable-line

  useEffect(() => { fetchData() }, [fetchData])

  const toggleBenchmark = (sym: BenchmarkSymbol) => {
    setSelectedBenchmarks(prev =>
      prev.includes(sym)
        ? prev.length > 1 ? prev.filter(b => b !== sym) : prev
        : [...prev, sym],
    )
  }

  // Build chart data from series
  const chartData = (data?.series ?? []).map(pt => ({
    date: pt.date.slice(5),  // MM-DD
    ...pt.values,
  })) ?? []

  // Build rolling sharpe data
  const rollingData = (() => {
    if (!data?.rolling_sharpe.length) return []
    // Pivot: [{date, portfolio, SPY, ...}]
    const byDate: Record<string, Record<string, number>> = {}
    for (const pt of data.rolling_sharpe) {
      if (!byDate[pt.date]) byDate[pt.date] = {}
      byDate[pt.date][pt.symbol] = pt.sharpe
    }
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date: date.slice(5), ...vals }))
  })()

  const visibleSymbols = ['portfolio', ...selectedBenchmarks]

  const tickInterval = Math.max(1, Math.floor((chartData.length) / 8))

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="card p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Benchmark selector */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-text-muted self-center mr-1">Benchmarks:</span>
          {ALL_BENCHMARKS.map(sym => {
            const active = selectedBenchmarks.includes(sym)
            return (
              <button
                key={sym}
                type="button"
                onClick={() => toggleBenchmark(sym)}
                className={clsx(
                  'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                  active ? `bench-pill-${sym} text-white` : 'bg-surface-DEFAULT text-text-muted hover:text-text-secondary',
                )}
              >
                {active ? <Minus size={10} /> : <Plus size={10} />}
                {sym}
              </button>
            )
          })}
        </div>
        {/* Period selector */}
        <div className="flex gap-1 ml-auto">
          {PERIODS.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                period === p ? 'bg-blue text-white' : 'text-text-muted hover:text-text-secondary',
              )}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="btn-ghost p-1.5 ml-1"
          aria-label="Refresh benchmark data"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && (
        <div className="py-12 text-center text-text-muted text-sm">
          <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue" />
          Fetching benchmark data via yfinance…
        </div>
      )}

      {error && (
        <div className="card p-4 text-rose-light text-sm">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          {/* Normalised performance chart */}
          <div className="card p-5">
            <h3 className="font-semibold text-text-primary mb-1 text-sm">
              Portfolio vs Benchmarks — Normalised to 100
            </h3>
            <p className="text-xs text-text-muted mb-4">
              {data.series[0]?.date} → {data.series[data.series.length - 1]?.date}
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9 }}
                  tickLine={false}
                  axisLine={false}
                  interval={tickInterval}
                />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={42} domain={['auto', 'auto']} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [v.toFixed(1), '']} />
                <ReferenceLine y={100} stroke="#475569" strokeDasharray="3 3" />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {visibleSymbols.map(sym => (
                  <Line
                    key={sym}
                    type="monotone"
                    dataKey={sym}
                    stroke={BENCHMARK_COLORS[sym] ?? '#94a3b8'}
                    strokeWidth={sym === 'portfolio' ? 2.5 : 1.5}
                    dot={false}
                    name={BENCHMARK_LABELS[sym] ?? sym}
                    strokeDasharray={sym === 'portfolio' ? undefined : '4 2'}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Metrics comparison table */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="panel-title">Performance Metrics</h3>
              <div className="flex gap-3 text-xs text-text-muted">
                <span>
                  Alpha vs {data.primary_benchmark}:{' '}
                  <strong className={data.alpha >= 0 ? 'text-emerald' : 'text-rose-light'}>
                    {data.alpha >= 0 ? '+' : ''}{data.alpha.toFixed(2)}%
                  </strong>
                </span>
                <span>
                  Beta: <strong className="text-text-primary">{data.beta.toFixed(3)}</strong>
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label="Benchmark comparison metrics">
                <thead>
                  <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
                    <th className="py-2 px-3 text-left font-medium">Metric</th>
                    {data.metrics.map(m => (
                      <th
                        key={m.symbol}
                        className={`py-2 px-3 text-right font-medium bench-col-${m.symbol}`}
                      >
                        {BENCHMARK_LABELS[m.symbol] ?? m.symbol}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'cumulative_return_pct', label: 'Total Return', fmt: (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`, color: true },
                    { key: 'annualized_return_pct', label: 'Ann. Return', fmt: (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`, color: true },
                    { key: 'sharpe_ratio', label: 'Sharpe Ratio', fmt: (v: number) => v.toFixed(2), color: false },
                    { key: 'max_drawdown_pct', label: 'Max Drawdown', fmt: (v: number) => `-${v.toFixed(1)}%`, color: false },
                    { key: 'volatility_pct', label: 'Volatility', fmt: (v: number) => `${v.toFixed(1)}%`, color: false },
                    { key: 'current_value', label: 'End Value', fmt: (v: number) => v.toFixed(1), color: false },
                  ].map(row => (
                    <tr key={row.key} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                      <td className="py-2.5 px-3 text-text-secondary font-medium text-xs">{row.label}</td>
                      {data.metrics.map(m => {
                        const val = m[row.key as keyof typeof m] as number
                        const isPortfolio = m.symbol === 'portfolio'
                        return (
                          <td
                            key={m.symbol}
                            className={clsx(
                              'py-2.5 px-3 text-right font-mono text-xs',
                              isPortfolio ? 'font-bold' : '',
                              row.color ? (val >= 0 ? 'text-emerald' : 'text-rose-light') : 'text-text-primary',
                            )}
                          >
                            {row.fmt(val)}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rolling Sharpe chart */}
          {rollingData.length > 0 && (
            <div className="card p-5">
              <h3 className="panel-title mb-4">Rolling 30-Day Sharpe Ratio</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={rollingData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} interval={Math.max(1, Math.floor(rollingData.length / 6))} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={42} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [v.toFixed(2), 'Sharpe']} />
                  <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="portfolio" stroke="#3b82f6" strokeWidth={2} dot={false} name="Portfolio" />
                  {selectedBenchmarks.slice(0, 1).map(sym => (
                    <Line key={sym} type="monotone" dataKey={sym} stroke={BENCHMARK_COLORS[sym]} strokeWidth={1.5} dot={false} name={BENCHMARK_LABELS[sym] ?? sym} strokeDasharray="4 2" />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Factor Exposure */}
          {factors && (
            <div className="card p-5">
              <h3 className="panel-title mb-4">Factor Exposure</h3>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-5">
                <div className="text-center">
                  <p className="text-xs text-text-muted">Market Beta</p>
                  <p className="text-lg font-bold text-text-primary mt-0.5">{factors.market_beta.toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-text-muted">Size Tilt</p>
                  <p className="text-sm font-bold text-blue mt-0.5">{factors.size_tilt}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-text-muted">Style Tilt</p>
                  <p className="text-sm font-bold text-amber mt-0.5">{factors.style_tilt}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-text-muted">Momentum</p>
                  <p className={clsx('text-sm font-bold mt-0.5', factors.momentum_score >= 0 ? 'text-emerald' : 'text-rose-light')}>
                    {factors.momentum_score >= 0 ? '+' : ''}{factors.momentum_score.toFixed(3)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-text-muted">Quality</p>
                  <p className="text-sm font-bold text-text-primary mt-0.5">{factors.quality_score.toFixed(2)}</p>
                </div>
              </div>
              <div className="space-y-2.5">
                {factors.factors.map(f => {
                  const rawPct = Math.abs(f.exposure) / 1.5 * 100
                  const snapped = Math.min(Math.round(rawPct / 5) * 5, 100)
                  return (
                    <div key={f.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-text-secondary font-medium">{f.name}</span>
                        <span className={clsx('text-xs font-mono font-bold', f.exposure >= 0 ? 'text-emerald' : 'text-rose-light')}>
                          {f.exposure >= 0 ? '+' : ''}{f.exposure.toFixed(3)}
                        </span>
                      </div>
                      <div className="h-1.5 bg-surface-DEFAULT rounded-full overflow-hidden">
                        <div
                          className={clsx(
                            'h-full rounded-full',
                            f.exposure >= 0 ? 'bg-emerald' : 'bg-rose-light',
                            `factor-bar-${snapped}`,
                          )}
                        />
                      </div>
                      <p className="text-xs text-text-muted mt-0.5">{f.description}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function PortfolioPage() {
  const { snapshot, fetchSnapshot } = usePortfolio()
  const { summary, fetchSummary } = useAccounts()
  const [showRebalance, setShowRebalance] = useState(false)
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get('tab')
    return TABS.some(x => x.key === t) ? (t as Tab) : 'overview'
  })

  // Analytics state
  const [allocation, setAllocation] = useState<AllocationBreakdown | null>(null)
  const [concentration, setConcentration] = useState<ConcentrationAnalysis | null>(null)
  const [correlation, setCorrelation] = useState<CorrelationMatrix | null>(null)
  const [performance, setPerformance] = useState<PerformanceData | null>(null)
  const [riskDecomp, setRiskDecomp] = useState<RiskDecomposition | null>(null)
  const [dividends, setDividends] = useState<DividendSchedule | null>(null)
  const [householdSummaryData, setHouseholdSummaryData] = useState<HouseholdSummary | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(true)

  useEffect(() => {
    fetchSnapshot()
    fetchSummary()

    setAnalyticsLoading(true)
    Promise.all([
      getPortfolioAllocation().then(setAllocation).catch(() => {}),
      getPortfolioConcentration().then(setConcentration).catch(() => {}),
      getPortfolioCorrelation().then(setCorrelation).catch(() => {}),
      getPortfolioPerformance().then(setPerformance).catch(() => {}),
      getPortfolioRiskDecomposition().then(setRiskDecomp).catch(() => {}),
      getPortfolioDividendSchedule().then(setDividends).catch(() => {}),
      getHouseholdSummary().then(setHouseholdSummaryData).catch(() => {}),
    ]).finally(() => setAnalyticsLoading(false))
  }, []) // eslint-disable-line

  const loading = !snapshot || analyticsLoading

  return (
    <div className="space-y-6 animate-fade-in">
      {showRebalance && <RebalanceModal onClose={() => setShowRebalance(false)} />}

      <PageHeader
        title="Portfolio Analytics"
        subtitle={snapshot ? 'How your investments are doing, across every account' : 'Loading...'}
        actions={
          <button
            onClick={() => setShowRebalance(true)}
            className="btn-primary gap-2"
            aria-label="Open rebalance dialog"
          >
            <RefreshCw size={14} aria-hidden />
            Rebalance
          </button>
        }
      />

      {/* Hero — the page's one number */}
      {snapshot && (
        <div className="card p-5">
          <p className="panel-title">Total portfolio value</p>
          <div className="flex items-end gap-3 flex-wrap mt-1">
            <p className="stat-hero text-text-primary">{fmt(snapshot.total_value)}</p>
            {/* null = not enough recorded history. Rendering it as 0 would
                show a green "+0.0% today" that reads as a real flat day. */}
            {snapshot.daily_change === null || snapshot.daily_change_pct === null ? (
              <span className="mb-2 text-text-secondary text-sm">
                today's change not available yet
              </span>
            ) : (
              <span className={clsx('mb-2', snapshot.daily_change >= 0 ? 'delta-chip-pos' : 'delta-chip-neg')}>
                {snapshot.daily_change >= 0 ? '+' : ''}{fmt(snapshot.daily_change)} ({pct(snapshot.daily_change_pct)}) today
              </span>
            )}
          </div>
        </div>
      )}

      {/* Discretionary / View-only indicator */}
      {summary && (
        <div className="flex items-center gap-4 px-1">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald shrink-0" aria-hidden />
            <span className="text-emerald font-medium">Discretionary:</span>
            <span className="text-text-primary font-mono">{fmt(summary.discretionary_value)}</span>
          </div>
          <span className="text-text-muted text-xs">|</span>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="inline-block w-2 h-2 rounded-full bg-text-muted shrink-0" aria-hidden />
            <span className="text-text-muted font-medium">View Only:</span>
            <span className="text-text-primary font-mono">{fmt(summary.non_discretionary_value)}</span>
          </div>
          <div className="flex-1 h-1.5 bg-surface-hover rounded-full overflow-hidden flex min-w-0" role="presentation">
            <div className="h-full bg-emerald" style={{ width: `${summary.discretionary_pct}%` }} />
            <div className="h-full bg-text-muted/40 flex-1" />
          </div>
          <span className="text-xs text-text-muted shrink-0">{summary.discretionary_pct.toFixed(1)}%</span>
        </div>
      )}

      {/* Summary cards */}
      {snapshot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
          <div className="card p-4">
            <p className="panel-title">Invested</p>
            <p className="stat-value mt-1">{fmt(snapshot.invested_amount)}</p>
          </div>
          <div className="card p-4">
            <p className="panel-title">Cash</p>
            <p className="stat-value mt-1">{fmt(snapshot.cash_position)}</p>
          </div>
          <div className="card p-4">
            <p className="panel-title">YTD Return</p>
            {/* Daily snapshots only begin 2026-07-29, so there is no
                January baseline to compute a YTD return from. Say so
                rather than show a 0% that looks measured. */}
            {snapshot.ytd_return === null ? (
              <p className="stat-value mt-1 text-text-secondary">—</p>
            ) : (
              <p className={clsx('stat-value mt-1', snapshot.ytd_return >= 0 ? 'text-emerald' : 'text-rose-light')}>
                {snapshot.ytd_return >= 0 ? '+' : ''}{pct(snapshot.ytd_return)}
              </p>
            )}
          </div>
          <div className="card p-4">
            <p className="panel-title">Holdings</p>
            <p className="stat-value mt-1">{snapshot.holdings.length}</p>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              tab === t.key
                ? 'text-blue border-blue'
                : 'text-text-muted border-transparent hover:text-text-secondary',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <DataSection
        loading={loading}
        empty={false}
        skeletonRows={8}
      >
      <div className="space-y-6">

      {/* ── Overview Tab ────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <>
          {/* Allocation donuts */}
          {allocation && (
            <div className="grid md:grid-cols-3 gap-4">
              <DonutChart title="By Sector" data={allocation.by_sector} />
              <DonutChart title="By Region" data={allocation.by_region} />
              <DonutChart title="By Asset Type" data={allocation.by_asset_type} />
            </div>
          )}

          {/* Concentration */}
          <div className="grid md:grid-cols-2 gap-4">
            {concentration && <ConcentrationSection data={concentration} />}
            {correlation && <CorrelationHeatmap data={correlation} />}
          </div>

          {/* Concentration risk warnings (household-level, from /api/accounts/summary) */}
          {householdSummaryData && (householdSummaryData.concentration_warnings?.length ?? 0) > 0 && (
            <ConcentrationRiskSection warnings={householdSummaryData.concentration_warnings!} />
          )}

          {/* Top 10 holdings across all accounts */}
          {householdSummaryData && (householdSummaryData.top_holdings?.length ?? 0) > 0 && (
            <TopHoldingsTable holdings={householdSummaryData.top_holdings!} />
          )}

          {/* Holdings table */}
          {snapshot && (
            <HoldingsTable holdings={snapshot.holdings} limit={DEFAULT_PROFILE.position_limit_gbp} />
          )}
        </>
      )}

      {/* ── Holdings Tab (flat all-accounts view, ex-/app/holdings) ─────── */}
      {tab === 'holdings' && (
        <Suspense fallback={<div className="p-8 text-text-muted">Loading holdings...</div>}>
          <HoldingsTab />
        </Suspense>
      )}

      {/* ── Performance Tab ─────────────────────────────────────────────── */}
      {tab === 'performance' && (
        <>
          {performance && <PerformanceSection data={performance} />}
          {snapshot && (
            <HoldingsTable holdings={snapshot.holdings} limit={DEFAULT_PROFILE.position_limit_gbp} />
          )}
        </>
      )}

      {/* ── Risk Tab ────────────────────────────────────────────────────── */}
      {tab === 'risk' && (
        <>
          {riskDecomp && <RiskSection data={riskDecomp} />}
          {correlation && <CorrelationHeatmap data={correlation} />}
          {snapshot && snapshot.risk_metrics && (
            <div className="card p-5">
              <h3 className="panel-title mb-4">Risk Metrics Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(snapshot.risk_metrics).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs text-text-muted capitalize">{k.replace(/_/g, ' ')}</p>
                    <p className="text-sm font-bold text-text-primary mt-0.5">
                      {typeof v === 'number' && v > 1000 ? fmt(v) : typeof v === 'number' ? v.toFixed(2) : String(v)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Dividends Tab ───────────────────────────────────────────────── */}
      {tab === 'dividends' && (
        <>
          {dividends && <DividendSection data={dividends} />}
        </>
      )}

      {/* ── Benchmark Tab ───────────────────────────────────────────────── */}
      {tab === 'benchmark' && <BenchmarkTab />}

      {/* ── Tax Tab ─────────────────────────────────────────────────────── */}
      {tab === 'tax' && <TaxTab />}

      {/* ── Rebalance Tab ───────────────────────────────────────────────── */}
      {tab === 'rebalance' && <RebalanceTab />}

      {/* ── Strategy Tab ────────────────────────────────────────────────── */}
      {tab === 'strategy' && <StrategyTab />}

      {/* ── Accounts Tab ────────────────────────────────────────────────── */}
      {tab === 'accounts' && <AccountsTab />}

      </div>{/* end inner space-y-6 */}
      </DataSection>

      {/* AI advisor */}
      <SmartAdvisorPanel />
    </div>
  )
}
